  // ✅ Trekkin.app — usePanelsStore.jsx v7.0.1 (ROLE → PANEL → FUNÇÕES + EVENT REGISTER)
  // 🔥 Base = v6.6.4 (EVENT EDIT REGISTERED)
  // 🔥 Mantém GPS + EVENTS + eventPayload + RolePermissions
  // 🔥 Mantém PanelMapSelectPoint e PanelEventEdit registados
  // 🔥 openPanel evita duplicar o mesmo painel na stack
  // 🔥 Sistema dinâmico de permissões (free/explorer/adventure/guide/admin)
  // 🔥 can(panelId, funcId) + canOpen(panelId)
  // 🔥 Listener Firebase em /permissions/{role}/{PanelId}/{funcId}

import { create } from "zustand";
import { useHeartRateStore } from "./useHeartRateStore.js";
import { useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import haversine from "haversine-distance";

  // 🔹 Firebase para permissões
  import { ref, onValue, off, get as getDb, update as updateDb, runTransaction } from "firebase/database";
import { db } from "../firebaseConfig";
import { isHistoryEntryOwnedByUser } from "../utils/historyOwnership.js";
import { awardRecordingDistanceXp } from "../utils/gamificationXp.js";
import { estimateActiveCalories } from "../utils/calorieEstimator.js";
import { useActivityModeStore } from "./useActivityModeStore.jsx";
import {
  ACTIVITY_MODE_WALK,
  isActivityModeCompatible,
  normalizeActivityMode,
} from "../utils/activityMode.js";

const PANEL_NAMES = [
  "intro",
  "login",
  "resetPassword",
  "nav",
  "settings",
  "setNewPassword",
  "navRealtime",
  "trailDetails",
  "trail",
  "groupChat",
  "groupManage",
  "groupTrack",
  "globalChat",
  "directChat",
  "guideDirectChats",
  "guide",
  "history",
  "activitySummary",
  "configurator",
  "admin",
  "adminCustomPush",
  "adminRtdbTraffic",
  "adminGamification",
  "adminSalesVouchers",
  "adminGuideRatings",
  "alertsAdmin",
  "adminWaypoints",
  "waypoints",
  "profile",
  "liveShare",
  "alertsCreate",
  "adminNavGrid",
  "subscriptions",
  "eventList",
  "eventDetails",
  "eventEnrollSuccess",
  "eventManage",
  "eventCreate",
  "eventEdit",
  "eventUploadReceipt",
  "eventRegister",
  "rolePermissions",
  "adminReports",
  "mapSelectPoint",
  "gamification",
  "adminPublicGpx",
  "publicTrails",
  "issueReport",
  "myIssueReports",
  "partnerReservations",
  "bikeService",
  "bikeRental",
];
const PANEL_SET = new Set(PANEL_NAMES);
const PANEL_IDS = {
  intro: "IntroScreen",
  login: "PanelLogin",
  resetPassword: "PanelResetPassword",
  nav: "PanelNav",
  settings: "SettingsPanel",
  setNewPassword: "PanelSetNewPassword",
  navRealtime: "PanelNavRealtime",
  trailDetails: "PanelTrailDetails",
  trail: "PanelTrail",
  groupChat: "PanelGroupChat",
  groupManage: "PanelGroupManage",
  groupTrack: "PanelGroupTrack",
  globalChat: "PanelGlobalChat",
  directChat: "PanelDirectChat",
  guideDirectChats: "PanelGuideDirectChats",
  guide: "PanelGuide",
  history: "PanelHistory",
  activitySummary: "PanelActivitySummary",
  configurator: "PanelConfigurator",
  admin: "PanelAdmin",
  adminCustomPush: "PanelAdminCustomPush",
  adminRtdbTraffic: "PanelAdminRtdbTraffic",
  adminGamification: "PanelAdminGamification",
  adminSalesVouchers: "PanelAdminSalesVouchers",
  adminGuideRatings: "PanelAdminGuideRatings",
  alertsAdmin: "PanelAlertsAdmin",
  adminWaypoints: "PanelWaypointsAdmin",
  waypoints: "PanelWaypoints",
  profile: "PanelProfile",
  liveShare: "PanelLiveShare",
  alertsCreate: "PanelAlertsCreate",
  adminNavGrid: "PanelNavGridAdmin",
  subscriptions: "PanelSubscriptions",
  eventList: "PanelEventList",
  eventDetails: "PanelEventDetails",
  eventEnrollSuccess: "PanelEventEnrollSuccess",
  eventManage: "PanelEventManage",
  eventCreate: "PanelEventCreate",
  eventEdit: "PanelEventEdit",
  eventUploadReceipt: "PanelEventUploadReceipt",
  eventRegister: "PanelEventRegister",
  rolePermissions: "PanelRolePermissions",
  adminReports: "PanelAdminReports",
  mapSelectPoint: "PanelMapSelectPoint",
  gamification: "PanelGamification",
  adminPublicGpx: "PanelAdminPublicGpx",
  publicTrails: "PanelPublicTrails",
  issueReport: "PanelIssueReport",
  myIssueReports: "PanelMyIssueReports",
  partnerReservations: "PanelPartnerReservations",
  bikeService: "PanelBikeService",
  bikeRental: "PanelBikeRental",
};

  // 🔹 Toast genérico
  import { showToast } from "../components/NotifyToast.jsx";

  // Stores externas
  import { useGroupStore } from "./useGroupStore.jsx";
  import { useAuthStore } from "./useAuthStore.jsx";
import { logEvent } from "./useLogsStore.jsx";
import {
  calculateTrackElevationStats,
  sanitizeElevationPoints,
} from "../utils/elevationStats.js";
import { logger } from "../utils/logger.js";
import {
  buildHistoryGpx,
  isIOSNativeContainer,
  isNativePlatform,
  readHistoryGpxNative,
  writeHistoryGpxNative,
} from "../utils/historyNative.js";
import {
  MAX_GAMIFICATION_AVG_SPEED_KMH,
  isAverageSpeedValidForGamification,
} from "../utils/gamificationRules.js";
import { filterAndroidTrackPoints } from "../utils/androidTrackFilter.js";
import {
  calculateRecordingMetrics,
  prepareRecordingMetricPoints,
} from "../utils/recordingMetrics.js";
import {
  clearNavigationSession,
  loadNavigationSession,
  saveNavigationSession,
} from "../utils/navigationSession.js";

const ROOT_PANEL = "nav";
const LAST_KNOWN_POS_KEY = "trekkin_last_known_pos_v1";
const RECORDING_DRAFT_KEY = "trekkin_recording_draft_v1";
const RECORDING_JOURNAL_KEY = "trekkin_recording_journal_v1";
const RECORDING_MODE_NORMAL = "normal";
const RECORDING_MODE_EXPEDITION = "expedition";
const IOS_RECORDING_GPS_PROFILE_KEY = "trekkin.ios.recordingGpsProfile";
const RECORDING_DRAFT_MAX_POINTS = 20000;
const ANDROID_RECORDING_DRAFT_MAX_POINTS = 160;
const ANDROID_RECORDING_JOURNAL_MAX_POINTS = 320;
const RECORDING_DRAFT_SAVE_MIN_MS = 3500;
const RECORDING_DRAFT_SAVE_MIN_DELTA_POINTS = 4;
let lastKnownPosPersistTs = 0;
let lastRecordingDraftSaveTs = 0;
let lastRecordingDraftPointsCount = 0;

function formatHistoryDefaultName(ts = Date.now()) {
  const d = new Date(Number(ts) || Date.now());
  const pad = (n) => String(n).padStart(2, "0");
  const dd = pad(d.getDate());
  const mm = pad(d.getMonth() + 1);
  const yy = String(d.getFullYear()).slice(-2);
  const hh = pad(d.getHours());
  const min = pad(d.getMinutes());
  return `${dd}/${mm}/${yy} - ${hh}:${min}`;
}

function parseDistanceKmValue(value, fallback = 0) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : fallback;
  }
  if (typeof value === "string") {
    const raw = value.trim();
    if (!raw) return fallback;
    const n = Number(raw.replace(",", ".").replace(/[^0-9.-]+/g, ""));
    return Number.isFinite(n) ? n : fallback;
  }
  return fallback;
}

function persistLastKnownPosition(pos, ts = Date.now()) {
  try {
    if (!pos || !Number.isFinite(Number(pos.lat)) || !Number.isFinite(Number(pos.lon))) return;
    if (ts - lastKnownPosPersistTs < 2500) return;
    lastKnownPosPersistTs = ts;
    localStorage.setItem(
      LAST_KNOWN_POS_KEY,
      JSON.stringify({
        lat: Number(pos.lat),
        lon: Number(pos.lon),
        ts: Number(pos.ts || ts),
      })
    );
  } catch {}
}

function isIOSDevice() {
  if (typeof navigator === "undefined") return false;
  return (
    /iPad|iPhone|iPod/i.test(navigator.userAgent || "") ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

function isNativeCapacitorDevice() {
  try {
    return !!window?.Capacitor?.isNativePlatform?.();
  } catch {
    return false;
  }
}

function postIOSNativeBridge(action, payload = {}) {
  try {
    const capacitor = window?.Capacitor;
    const platform = capacitor?.getPlatform?.();
    const bridge = window?.webkit?.messageHandlers?.nativeBridge;
    const nav =
      typeof navigator !== "undefined"
        ? navigator
        : { userAgent: "", platform: "", maxTouchPoints: 0 };
    const isIOS =
      platform === "ios" ||
      /iPad|iPhone|iPod/i.test(nav.userAgent || "") ||
      (nav.platform === "MacIntel" && nav.maxTouchPoints > 1);
    if (!isIOS || !bridge?.postMessage) return false;
    bridge.postMessage({ action, ...payload });
    return true;
  } catch {
    return false;
  }
}

function getIOSRecordingGpsConfig() {
  let profile = "balanced";
  try {
    const raw = String(localStorage.getItem(IOS_RECORDING_GPS_PROFILE_KEY) || "").trim();
    if (["precision", "balanced", "battery"].includes(raw)) profile = raw;
  } catch {}

  if (profile === "precision") {
    return {
      gpsProfile: profile,
      accuracy: "navigation",
      distanceFilter: 0,
    };
  }

  if (profile === "battery") {
    return {
      gpsProfile: profile,
      accuracy: "tenMeters",
      distanceFilter: 10,
    };
  }

  return {
    gpsProfile: profile,
    accuracy: "best",
    distanceFilter: 5,
  };
}

function isProbablyOnline() {
  if (typeof navigator === "undefined") return true;
  return navigator.onLine !== false;
}

async function withTimeout(promise, ms = 8000, label = "op_timeout") {
  let timer = null;
  try {
    return await Promise.race([
      Promise.resolve(promise),
      new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error(label)), ms);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

function downsampleDraftPoints(points, maxPoints = RECORDING_DRAFT_MAX_POINTS) {
  if (!Array.isArray(points) || points.length <= maxPoints) return points || [];
  if (maxPoints <= 2) return [points[0], points[points.length - 1]].filter(Boolean);
  const sampled = [points[0]];
  const interiorTarget = Math.max(0, maxPoints - 2);
  const span = Math.max(1, points.length - 2);
  const step = span / Math.max(1, interiorTarget);
  for (let i = 0; i < interiorTarget; i += 1) {
    const idx = 1 + Math.min(span - 1, Math.floor(i * step));
    const item = points[idx];
    if (item && item !== sampled[sampled.length - 1]) sampled.push(item);
  }
  const last = points[points.length - 1];
  if (last && sampled[sampled.length - 1] !== last) sampled.push(last);
  return sampled;
}

function normalizeDraftPoints(points, maxPoints = RECORDING_DRAFT_MAX_POINTS) {
  if (!Array.isArray(points)) return [];
  const source =
    points.length > maxPoints
      ? downsampleDraftPoints(points, maxPoints)
      : points;
  const out = [];
  for (let i = 0; i < source.length; i += 1) {
    const p = source[i];
    const lat = Number(p?.lat);
    const lon = Number(p?.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
    const item = {
      lat,
      lon,
      ele: Number.isFinite(Number(p?.ele)) ? Number(p.ele) : null,
      acc: Number.isFinite(Number(p?.acc)) ? Number(p.acc) : null,
      spd: Number.isFinite(Number(p?.spd)) ? Number(p.spd) : 0,
      spdAcc: Number.isFinite(Number(p?.spdAcc)) ? Number(p.spdAcc) : null,
      ts: Number.isFinite(Number(p?.ts)) ? Number(p.ts) : Date.now(),
      seq: Number.isFinite(Number(p?.seq)) ? Number(p.seq) : undefined,
      gap: !!p?.gap,
    };
    out.push(item);
  }
  return out;
}

function readRecordingDraft() {
  try {
    const raw = localStorage.getItem(RECORDING_DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const points = normalizeDraftPoints(parsed?.points);
    const startTs = Number(parsed?.recordingStartTime || 0);
    const updatedAt = Number(parsed?.updatedAt || 0);
    const isRecording = !!parsed?.isRecording;
    if (!isRecording || !startTs || points.length < 2) return null;
    // ignora rascunhos muito antigos (4 dias)
    if (updatedAt && Date.now() - updatedAt > 4 * 24 * 60 * 60 * 1000) {
      return null;
    }
    return {
      isRecording: true,
      isRecordingPaused: !!parsed?.isRecordingPaused,
      recordingPausedAt: Number(parsed?.recordingPausedAt || 0) || null,
      recordingPausedDurationMs: Number(parsed?.recordingPausedDurationMs || 0) || 0,
      recordingMode:
        parsed?.recordingMode === RECORDING_MODE_EXPEDITION
          ? RECORDING_MODE_EXPEDITION
          : RECORDING_MODE_NORMAL,
      recordingSessionId:
        parsed?.recordingSessionId != null ? String(parsed.recordingSessionId) : null,
      recordingStartTime: startTs,
      points,
    };
  } catch {
    return null;
  }
}

function clearRecordingDraft() {
  try {
    localStorage.removeItem(RECORDING_DRAFT_KEY);
  } catch {}
  lastRecordingDraftSaveTs = 0;
  lastRecordingDraftPointsCount = 0;
}

function readRecordingJournal() {
  try {
    const raw = localStorage.getItem(RECORDING_JOURNAL_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const startTs = Number(parsed?.recordingStartTime || 0);
    const updatedAt = Number(parsed?.updatedAt || 0);
    const points = normalizeDraftPoints(parsed?.points);
    if (!startTs || points.length < 2) return null;
    if (updatedAt && Date.now() - updatedAt > 7 * 24 * 60 * 60 * 1000) return null;
    return {
      recordingStartTime: startTs,
      recordingSessionId:
        parsed?.recordingSessionId != null ? String(parsed.recordingSessionId) : null,
      points,
      updatedAt,
    };
  } catch {
    return null;
  }
}

function clearRecordingJournal() {
  try {
    localStorage.removeItem(RECORDING_JOURNAL_KEY);
  } catch {}
}

function appendRecordingJournalPoints(recordingStartTime, incomingPoints, recordingSessionId = null) {
  try {
    const startTs = Number(recordingStartTime || 0);
    if (!startTs) return;
    const isAndroid =
      typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent || "");
    const journalMax = isAndroid
      ? ANDROID_RECORDING_JOURNAL_MAX_POINTS
      : RECORDING_DRAFT_MAX_POINTS;
    const toAppend = normalizeDraftPoints(incomingPoints, journalMax);
    if (!toAppend.length) return;

    const existing = readRecordingJournal();
    const sameSession =
      existing && Number(existing.recordingStartTime || 0) === startTs;
    const base = sameSession ? existing.points : [];
    const merged = mergeRecoveredRecordingPoints(base, toAppend);
    if (merged.length < 1) return;

    localStorage.setItem(
      RECORDING_JOURNAL_KEY,
      JSON.stringify({
        v: 1,
        updatedAt: Date.now(),
        recordingStartTime: startTs,
        recordingSessionId:
          recordingSessionId != null
            ? String(recordingSessionId)
            : existing?.recordingSessionId != null
              ? String(existing.recordingSessionId)
              : null,
        points: downsampleDraftPoints(merged, journalMax),
      })
    );
  } catch {}
}

function persistRecordingDraft(state, opts = {}) {
  try {
    if (!state?.isRecording) return;
    const now = Date.now();
    const force = !!opts.force;
    const isAndroid =
      typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent || "");
    const rawPoints = Array.isArray(state.points) ? state.points : [];
    const rawLen = rawPoints.length;
    if (rawLen < 1) return;
    if (
      isAndroid &&
      !force &&
      now - lastRecordingDraftSaveTs < RECORDING_DRAFT_SAVE_MIN_MS &&
      Math.abs(rawLen - lastRecordingDraftPointsCount) <
        RECORDING_DRAFT_SAVE_MIN_DELTA_POINTS
    ) {
      return;
    }
    const points = normalizeDraftPoints(
      rawPoints,
      isAndroid ? ANDROID_RECORDING_DRAFT_MAX_POINTS : RECORDING_DRAFT_MAX_POINTS
    );
    if (points.length < 1) return;
    if (
      !isAndroid &&
      !force &&
      now - lastRecordingDraftSaveTs < RECORDING_DRAFT_SAVE_MIN_MS &&
      Math.abs(points.length - lastRecordingDraftPointsCount) <
        RECORDING_DRAFT_SAVE_MIN_DELTA_POINTS
    ) {
      return;
    }
    const payload = {
      v: 1,
      updatedAt: now,
      isRecording: true,
      isRecordingPaused: !!state.isRecordingPaused,
      recordingPausedAt: Number(state.recordingPausedAt || 0) || null,
      recordingPausedDurationMs: Number(state.recordingPausedDurationMs || 0) || 0,
      recordingMode:
        state.recordingMode === RECORDING_MODE_EXPEDITION
          ? RECORDING_MODE_EXPEDITION
          : RECORDING_MODE_NORMAL,
      recordingSessionId:
        state?.recordingSessionId != null ? String(state.recordingSessionId) : null,
      recordingStartTime: Number(state.recordingStartTime || now),
      points,
    };
    localStorage.setItem(RECORDING_DRAFT_KEY, JSON.stringify(payload));
    lastRecordingDraftSaveTs = now;
    lastRecordingDraftPointsCount = rawLen;
  } catch {}
}

function computeRecoveredDistance(points) {
  if (!Array.isArray(points) || points.length < 2) return 0;
  let dist = 0;
  for (let i = 1; i < points.length; i += 1) {
    const a = points[i - 1];
    const b = points[i];
    dist += haversine({ lat: a.lat, lon: a.lon }, { lat: b.lat, lon: b.lon });
  }
  return dist;
}

function mergeRecoveredRecordingPoints(...sources) {
  const merged = [];
  const seen = new Set();

  sources.forEach((list) => {
    const normalized = normalizeDraftPoints(list);
    normalized.forEach((point) => {
      const ts = Number(point?.ts || 0);
      if (!ts) return;
      const lat = Number(point?.lat);
      const lon = Number(point?.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
      const key = `${ts}:${lat.toFixed(6)}:${lon.toFixed(6)}:${point?.gap ? 1 : 0}`;
      if (seen.has(key)) return;
      seen.add(key);
      merged.push({
        lat,
        lon,
        ele: Number.isFinite(Number(point?.ele)) ? Number(point.ele) : null,
        acc: Number.isFinite(Number(point?.acc)) ? Number(point.acc) : null,
        spd: Number.isFinite(Number(point?.spd)) ? Number(point.spd) : 0,
        spdAcc: Number.isFinite(Number(point?.spdAcc)) ? Number(point.spdAcc) : null,
        ts,
        seq: Number.isFinite(Number(point?.seq)) ? Number(point.seq) : undefined,
        gap: !!point?.gap,
      });
    });
  });

  merged.sort((a, b) => (a.ts || 0) - (b.ts || 0));
  return merged;
}

const recoveredRecordingDraft = readRecordingDraft();
const recoveredRecordingJournal = readRecordingJournal();
const recoveredInitialPoints = mergeRecoveredRecordingPoints(
  recoveredRecordingDraft?.points || [],
  recoveredRecordingJournal?.points || []
);
const recoveredInitialStartTs = Number(
  recoveredRecordingDraft?.recordingStartTime ||
    recoveredRecordingJournal?.recordingStartTime ||
    Date.now()
);
const shouldResumeRecoveredRecording =
  recoveredInitialPoints.length > 1 && (isIOSDevice() || isNativeCapacitorDevice());
const recoveredInitialPaused =
  shouldResumeRecoveredRecording && !!recoveredRecordingDraft?.isRecordingPaused;
const recoveredInitialPausedAt =
  recoveredInitialPaused && Number(recoveredRecordingDraft?.recordingPausedAt || 0)
    ? Number(recoveredRecordingDraft?.recordingPausedAt || 0)
    : null;
const recoveredInitialPausedDurationMs = Number(
  recoveredRecordingDraft?.recordingPausedDurationMs || 0
);
const recoveredInitialSessionId =
  recoveredRecordingDraft?.recordingSessionId ||
  recoveredRecordingJournal?.recordingSessionId ||
  (recoveredInitialStartTs ? `rec-${recoveredInitialStartTs}` : null);

const navigationStorage =
  typeof localStorage !== "undefined" ? localStorage : null;
const recoveredNavigationSession = loadNavigationSession(navigationStorage);

if (
  typeof window !== "undefined" &&
  Array.isArray(recoveredNavigationSession?.route) &&
  recoveredNavigationSession.route.length > 1
) {
  window.__trekkinLastNavGridRoute = {
    path: recoveredNavigationSession.route,
    externalSegments: [],
    coloredSegments: [],
    restored: true,
    ts: Date.now(),
  };
}

function persistActiveNavigation(state) {
  try {
    const hasWaypoint = !!state?.navWaypointTarget || !!state?.navWaypointPending;
    const hasTrail = Array.isArray(state?.trailBase) && state.trailBase.length > 1;
    if (!hasWaypoint && !hasTrail) return null;
    return saveNavigationSession(
      navigationStorage,
      state,
      typeof window !== "undefined" ? window.__trekkinLastNavGridRoute : null
    );
  } catch {
    return null;
  }
}

function clearActiveNavigationPersistence() {
  clearNavigationSession(navigationStorage);
  if (typeof window !== "undefined") {
    window.__trekkinLastNavGridRoute = {
      path: [],
      externalSegments: [],
      coloredSegments: [],
      ts: Date.now(),
    };
  }
}

if (shouldResumeRecoveredRecording && !recoveredInitialPaused) {
  setTimeout(() => {
    postIOSNativeBridge("startRecordingLocation", {
      source: "recoveredRecording",
      ...getIOSRecordingGpsConfig(),
    });
  }, 0);
}

  function getAPIBase() {
    return import.meta.env.VITE_API_BASE;
  }

  function debounce(fn, wait = 800) {
    let t = null;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  }

  function sanitizeTrackPoints(points) {
    if (!Array.isArray(points)) return [];
    const cleaned = [];
    let lastTs = 0;
    let lastPoint = null;
    for (const p of points) {
      const lat = Number(p?.lat);
      const lon = Number(p?.lon ?? p?.lng);
      if (!p || !Number.isFinite(lat) || !Number.isFinite(lon)) continue;
      const ts = Number(p.ts || p.time || 0);
      if (!ts) continue;
      if (lastTs && ts <= lastTs) continue;
      if (lastPoint) {
        const dt = Math.max(0, (ts - lastTs) / 1000);
        const dist = haversine(
          { lat: lastPoint.lat, lon: lastPoint.lon },
          { lat: p.lat, lon: p.lon }
        );
        if (
          (dt < 2 && dist > 60) ||
          (dt < 5 && dist > 120) ||
          (dt < 30 && dist > 500) ||
          (dt < 120 && dist > 1000)
        ) {
          continue;
        }
      }
      const next = { ...p, lat, lon, ts };
      cleaned.push(next);
      lastPoint = next;
      lastTs = ts;
    }

    const withoutIsolatedSpikes = [];
    for (let i = 0; i < cleaned.length; i += 1) {
      const prev = withoutIsolatedSpikes[withoutIsolatedSpikes.length - 1] || null;
      const curr = cleaned[i];
      const next = cleaned[i + 1] || null;
      if (prev && curr && next) {
        const prevToCurr = haversine(
          { lat: prev.lat, lon: prev.lon },
          { lat: curr.lat, lon: curr.lon }
        );
        const currToNext = haversine(
          { lat: curr.lat, lon: curr.lon },
          { lat: next.lat, lon: next.lon }
        );
        const prevToNext = haversine(
          { lat: prev.lat, lon: prev.lon },
          { lat: next.lat, lon: next.lon }
        );
        const dtPrev = Math.max(0.001, (Number(curr.ts || 0) - Number(prev.ts || 0)) / 1000);
        const dtNext = Math.max(0.001, (Number(next.ts || 0) - Number(curr.ts || 0)) / 1000);
        const speedPrevKmh = (prevToCurr / dtPrev) * 3.6;
        const speedNextKmh = (currToNext / dtNext) * 3.6;
        const isOutAndBackSpike =
          prevToCurr > 350 &&
          currToNext > 350 &&
          prevToNext < Math.max(120, Math.min(prevToCurr, currToNext) * 0.12) &&
          speedPrevKmh > 80 &&
          speedNextKmh > 80;
        if (isOutAndBackSpike) {
          logEvent("warn", "HISTORY_GPS_SPIKE_REMOVED", {
            index: i,
            lat: curr.lat,
            lon: curr.lon,
            prevToCurrM: Math.round(prevToCurr),
            currToNextM: Math.round(currToNext),
            prevToNextM: Math.round(prevToNext),
            speedPrevKmh: Number(speedPrevKmh.toFixed(1)),
            speedNextKmh: Number(speedNextKmh.toFixed(1)),
          });
          continue;
        }
      }
      withoutIsolatedSpikes.push(curr);
    }

    return sanitizeElevationPoints(withoutIsolatedSpikes);
  }

  // 🔐 Helper para detetar role atual (flexível, não parte nada)
  function getCurrentRole() {
    try {
      const auth = useAuthStore.getState();
      const rawRole =
        auth?.role ||
        auth?.userRole ||
        auth?.planRole ||
        auth?.user?.role ||
        auth?.user?.plan ||
        auth?.user?.subscription;

      if (!rawRole) return "free";

      const r = String(rawRole).toLowerCase();

      if (["free", "explorer", "adventure", "guide", "admin"].includes(r)) {
        return r;
      }

      return "free";
    } catch {
      return "free";
    }
  }

  export const usePanelsStore = create((set, get) => ({
    //------------------------------------------------------------------
    // 🎛️ Gestão de painéis
    //------------------------------------------------------------------
    currentPanel: null,
    panelStack: [],
    lastOpenedPanels: {},
    isFullscreen: false,
    _isResetting: false,

    // --------------------------------------------------------------
    // 🧭 Navegação / waypoints
    // --------------------------------------------------------------
    navWaypointTarget:
      recoveredNavigationSession?.type === "waypoint"
        ? recoveredNavigationSession.target
        : null,
    navWaypointPending:
      recoveredNavigationSession?.type === "waypoint"
        ? recoveredNavigationSession.pending
        : null,
    navWaypointQueue:
      recoveredNavigationSession?.type === "waypoint"
        ? recoveredNavigationSession.queue || []
        : [],
    navWaypointAddMode: false,
    setNavWaypointTarget: (target) => {
      set({ navWaypointTarget: target || null, navWaypointPending: null });
      persistActiveNavigation(get());
    },
    setNavWaypointPending: (payload) => {
      set({ navWaypointPending: payload || null });
      persistActiveNavigation(get());
    },
    setNavWaypointAddMode: (on) => set({ navWaypointAddMode: !!on }),
    persistNavigationRoute: (routeSnapshot) => {
      if (typeof window !== "undefined") {
        window.__trekkinLastNavGridRoute = {
          path: Array.isArray(routeSnapshot?.path) ? routeSnapshot.path : [],
          externalSegments: Array.isArray(routeSnapshot?.externalSegments)
            ? routeSnapshot.externalSegments
            : [],
          coloredSegments: Array.isArray(routeSnapshot?.coloredSegments)
            ? routeSnapshot.coloredSegments
            : [],
          ts: Date.now(),
        };
      }
      persistActiveNavigation(get());
    },
    clearNavWaypointPending: () => {
      set({ navWaypointPending: null });
      persistActiveNavigation(get());
    },
    enqueueNavWaypoint: (target) => {
      set((state) => {
        if (!target) return state;
        const tId = String(target?.id || "").trim();
        const tLat = Number(target?.lat);
        const tLon = Number(target?.lon ?? target?.lng);
        const exists = state.navWaypointQueue.some((w) => {
          const wId = String(w?.id || "").trim();
          if (tId && wId && wId === tId) return true;
          const wLat = Number(w?.lat);
          const wLon = Number(w?.lon ?? w?.lng);
          return (
            Number.isFinite(tLat) &&
            Number.isFinite(tLon) &&
            Number.isFinite(wLat) &&
            Number.isFinite(wLon) &&
            Math.abs(wLat - tLat) < 1e-7 &&
            Math.abs(wLon - tLon) < 1e-7
          );
        });
        if (exists) {
          return state;
        }
        return { navWaypointQueue: [...state.navWaypointQueue, target] };
      });
      persistActiveNavigation(get());
    },
    advanceNavWaypoint: () => {
      set((state) => {
        const next = state.navWaypointQueue[0] || null;
        return {
          navWaypointTarget: next,
          navWaypointQueue: next
            ? state.navWaypointQueue.slice(1)
            : state.navWaypointQueue,
        };
      });
      persistActiveNavigation(get());
    },
    clearNavWaypoint: () => {
      set({
        navWaypointTarget: null,
        navWaypointQueue: [],
        navWaypointPending: null,
        navWaypointAddMode: false,
      });
      clearActiveNavigationPersistence();
    },

    // --------------------------------------------------------------
    // 🧭 Planeamento de rotas / waypoints
    // --------------------------------------------------------------
    planActive: false,
    planWaypoints: [],
    planStart: null,
    planEnd: null,
    planPickMode: null,
    planFinalizeRequested: false,
    planRoutePoints: [],
    planName: "",
    planRoutePreference: "shortest",
    planRouteType: "linear",
    startPlanRoute: () =>
      set({
        planActive: true,
        planWaypoints: [],
        planStart: null,
        planEnd: null,
        planPickMode: null,
        planFinalizeRequested: false,
        planRoutePoints: [],
        planName: "",
        planRoutePreference: "shortest",
        planRouteType: "linear",
      }),
    stopPlanRoute: () =>
      set({
        planActive: false,
        planWaypoints: [],
        planStart: null,
        planEnd: null,
        planPickMode: null,
        planFinalizeRequested: false,
        planRoutePoints: [],
        planName: "",
        planRoutePreference: "shortest",
        planRouteType: "linear",
      }),
    setPlanStart: (value) => set({ planStart: value ? { ...value } : null }),
    setPlanEnd: (value) => set({ planEnd: value ? { ...value } : null }),
    setPlanPickMode: (mode) => set({ planPickMode: mode || null }),
    setPlanFinalizeRequested: (flag) =>
      set({ planFinalizeRequested: !!flag }),
    setPlanWaypoints: (list) =>
      set({
        planWaypoints: Array.isArray(list) ? [...list] : [],
      }),
    addPlanWaypoint: (waypoint) =>
      set((state) => {
        if (
          !waypoint ||
          !Number.isFinite(Number(waypoint?.lat)) ||
          !Number.isFinite(Number(waypoint?.lon))
        ) {
          return state;
        }
        const current = Array.isArray(state.planWaypoints)
          ? state.planWaypoints
          : [];
        if (waypoint.id && current.some((w) => w.id === waypoint.id)) {
          return state;
        }
        return {
          planWaypoints: [...current, { ...waypoint }],
        };
      }),
    removePlanWaypoint: (id) =>
      set((state) => ({
        planWaypoints: (state.planWaypoints || []).filter(
          (wp) => wp.id !== id
        ),
      })),
    setPlanRoutePoints: (points) =>
      set({
        planRoutePoints: Array.isArray(points) ? [...points] : [],
      }),
    setPlanName: (name) => set({ planName: name || "" }),
    setPlanRoutePreference: (value) =>
      set({
        planRoutePreference:
          value === "low_ascent" ? "low_ascent" : "shortest",
      }),
    setPlanRouteType: (value) =>
      set({
        planRouteType: value === "circular" ? "circular" : "linear",
        ...(value === "circular" ? { planEnd: null } : {}),
      }),

    //------------------------------------------------------------------
    // 🔐 Permissões dinâmicas (ROLE → PANEL → FUNÇÕES)
    //------------------------------------------------------------------
    permissions: {
      free: {},
      explorer: {},
      adventure: {},
      guide: {},
      admin: {},
    },
    _permissionsRole: null,
    _permissionsRef: null,
    permissionsReady: false,
    _allPermissionsLoaded: false,

    // Define o role ativo e liga o listener ao Firebase
    setPermissionsRole: (role) => {
      const safeRole = role || getCurrentRole();
      const state = get();

      if (state._permissionsRole === safeRole && state._permissionsRef) {
        // Já estamos a ouvir este role
        return;
      }

      // Desligar listener anterior, se existir
      if (state._permissionsRef) {
        try {
          off(state._permissionsRef);
        } catch (e) {
          console.warn("⚠️ Falha ao remover listener de permissões anterior:", e);
        }
      }

      if (!db) {
        console.warn("⚠️ Firebase DB indisponível para permissões.");
        set({ _permissionsRole: safeRole, _permissionsRef: null });
        return;
      }

      const r = ref(db, `/permissions/${safeRole}`);
      onValue(
        r,
        (snap) => {
          const value = snap.val() || {};
          set((s) => ({
            permissions: {
              ...s.permissions,
              [safeRole]: value || {},
            },
            _permissionsRole: safeRole,
            _permissionsRef: r,
            permissionsReady: true,
          }));
          console.log(
            `🔐 Permissões carregadas para role='${safeRole}':`,
            Object.keys(value || {})
          );
        },
        (err) => {
          console.warn("⚠️ Erro no listener de permissões:", err);
          set({ permissionsReady: false, _permissionsRole: safeRole });
        }
      );
    },

    // --------------------------------------------------
    // 🧩 LOAD ALL PERMISSIONS (para Subscrições / Comparação)
    // --------------------------------------------------
    loadAllPermissions: () => {
      if (!db) {
        console.warn("⚠️ Firebase DB indisponível (loadAllPermissions)");
        return;
      }

      if (get()._allPermissionsLoaded) return;

      const roles = ["free", "explorer", "adventure", "guide"];

      Promise.all(
        roles.map(async (role) => {
          try {
            const snap = await getDb(ref(db, `/permissions/${role}`));
            return [role, snap.val() || {}];
          } catch (err) {
            console.warn(`⚠️ Erro a carregar permissões role=${role}`, err);
            return [role, {}];
          }
        })
      ).then((entries) => {
        const nextPermissions = { ...get().permissions };
        entries.forEach(([role, value]) => {
          nextPermissions[role] = value;
          console.log(
            `📦 Permissões globais carregadas: role='${role}'`,
            Object.keys(value || {})
          );
        });

        set({
          permissions: nextPermissions,
          _allPermissionsLoaded: true,
        });
      });
    },

    // Verifica se role atual pode executar funcId num PanelId (nome do componente, ex: PanelNav)
    can: (panelId, funcId = "open") => {
      const role = getCurrentRole();

      // Admin é superuser
      if (role === "admin") {
        return { ok: true, role, panelId, funcId, reason: "admin_bypass" };
      }

      const { permissions } = get();
      const rolePerms = permissions?.[role] || {};
      const panelPerms = rolePerms?.[panelId] || {};
      const val = panelPerms?.[funcId];

      // Sem entrada explícita → modo permissivo
      if (val === undefined || val === null) {
        return { ok: true, role, panelId, funcId, reason: "implicit_allow" };
      }

      if (val === false) {
        return { ok: false, role, panelId, funcId, reason: "explicit_deny" };
      }

      return { ok: true, role, panelId, funcId, reason: "explicit_allow" };
    },

    // Helper para abertura de painéis
    canOpen: (panelId) => {
      const state = get();
      if (typeof state.can !== "function") {
        return { ok: true, reason: "no_can_function" };
      }
      return state.can(panelId, "open");
    },

    //------------------------------------------------------------------
    // 📂 openPanel (com validação de permissões por painel + pickPoint)
    //------------------------------------------------------------------
    openPanel: (name, props = {}) => {
      if (!PANEL_SET.has(name)) {
        console.warn(`⚠️ Painel desconhecido: ${name}`);
        return;
      }

      const { panelStack, currentPanel, canOpen } = get();

      // Painéis base nunca são bloqueados (segurança/login)
      // Assistência e aluguer têm autorização contextual própria no Firebase
      // (cliente, parceiro associado ou admin). Não devem depender da matriz
      // histórica de painéis, que não contém estes módulos recentes.
      const skipPermissionCheck = ["intro", "login", "bikeService", "bikeRental"].includes(name);

      const panelId = PANEL_IDS[name] || name;
      const isRoutePlanner = name === "waypoints" && props?.mode === "planRoute";
      // O aluguer faz parte da mesma área funcional da assistência/equipamentos.
      // Reutiliza a permissão já existente para não ficar bloqueado em instalações
      // cuja matriz de permissões foi criada antes de existir PanelBikeRental.
      const accessPanelId = isRoutePlanner
        ? "PanelNav"
        : name === "bikeRental"
          ? "PanelBikeService"
          : panelId;
      const accessFunctionId = isRoutePlanner ? "plan_route" : "open";

      if (!skipPermissionCheck && typeof canOpen === "function") {
        const check = isRoutePlanner
          ? get().can(accessPanelId, accessFunctionId)
          : canOpen(accessPanelId);
        if (!check.ok) {
          console.warn(
            `🚫 Acesso negado ao painel '${accessPanelId}' para role='${check.role}' (func='${accessFunctionId}')`
          );
          try {
            showToast(
              "🚫 Não tens permissão para abrir este painel. Verifica o teu plano ou fala com o guia/admin."
            );
          } catch {
            // fallback silencioso
          }
          return;
        }
      }

      // --------------------------------------------------------------
      // 🧭 PICK POINT MODE (para PanelMapSelectPoint)
      // --------------------------------------------------------------
      if (typeof window !== "undefined") {
        if (name === "mapSelectPoint") {
          // pequeno delay para garantir que o Leaflet já está pronto
          setTimeout(() => {
            console.log("📌 [usePanelsStore] enable pickPoint (mapSelectPoint)");
            window.dispatchEvent(
              new CustomEvent("trekkin:enable-pickpoint")
            );
          }, 50);
        } else {
          // qualquer outro painel → desativar modo pickPoint
          window.dispatchEvent(
            new CustomEvent("trekkin:disable-pickpoint")
          );
        }
      }

      // --------------------------------------------------------------
      // Reabrir o mesmo painel com novos props
      // --------------------------------------------------------------
      if (currentPanel?.name === name) {
        console.log(`🔄 Painel '${name}' reaberto com novos props`);

        const newPanel = { name, props: { ...props } };
        const { panelStack } = get();
        const newStack = [...panelStack];
        newStack[newStack.length - 1] = newPanel;

        set({
          currentPanel: newPanel,
          panelStack: newStack,
          eventPayload: null,
        });

        return;
      }

      if (get()._isResetting) {
        console.warn(`⚠️ Ignorado openPanel('${name}') durante reset`);
        return;
      }

      // fallback se abrir groupChat sem grupos
      if (name === "groupChat") {
        const gs = useGroupStore.getState();
        const groupList = gs.mygroups || gs.myGroups || gs.groups || [];
        const requestedGroupId = String(props?.groupId || "").trim();
        if (!requestedGroupId && (!Array.isArray(groupList) || groupList.length === 0)) {
          console.info("ℹ️ Sem grupos — abrir Chat Global");
          return get().openPanel("globalChat");
        }
      }

      const isFullCover = ["intro", "login"].includes(name);
      const newPanel = { name, props: { ...props } };

      let newStack = isFullCover ? [] : [...panelStack];

      // não filtrar — stack mantém histórico correto
      newStack.push(newPanel);

      set({
        currentPanel: newPanel,
        panelStack: newStack,
      });

      console.log(
        `🟩 Painel aberto: ${name}${isFullCover ? " (stack limpa)" : ""}`
      );
    },

    goBack: () => {
      const { panelStack } = get();
      if (panelStack.length <= 1) {
        console.log("🏞️ Nenhum painel anterior — mapa");
        set({ currentPanel: null, panelStack: [] });
        return;
      }
      const newStack = panelStack.slice(0, -1);
      set({
        currentPanel: newStack[newStack.length - 1],
        panelStack: newStack,
      });
      console.log("⬅️ Voltar painel");
    },

    replacePanel: (name, props = {}) => {
      if (!name) return;
      const { panelStack } = get();
      const previousStack = panelStack.slice(0, -1);
      const previousPanel = previousStack[previousStack.length - 1];
      const nextPanel = previousPanel?.name === name
        ? { ...previousPanel, props: { ...(previousPanel.props || {}), ...props } }
        : { name, props: { ...props } };
      const nextStack = previousPanel?.name === name
        ? [...previousStack.slice(0, -1), nextPanel]
        : [...previousStack, nextPanel];
      set({ currentPanel: nextPanel, panelStack: nextStack });
      console.log(`↩️ Painel substituído por: ${name}`);
    },

    closePanel: () => {
      const { currentPanel } = get();
      if (currentPanel?.name === "nav") {
        console.log("🗺️ Fechar nav → mapa");
        set({ currentPanel: null, panelStack: [] });
        return;
      }
      get().goBack();
    },

    openRoot: () =>
      set({
        currentPanel: { name: ROOT_PANEL, props: {} },
        panelStack: [{ name: ROOT_PANEL, props: {} }],
      }),

    clickOutside: () => {
      const { currentPanel } = get();
      if (currentPanel?.name && currentPanel.name !== ROOT_PANEL) {
        get().openRoot();
      }
    },

    //------------------------------------------------------------------
    // 🛠️ toggleButtonPanel
    //------------------------------------------------------------------
    toggleButtonPanel: (key, name) => {
      const state = get();

      if (state.currentPanel?.name === name) {
        console.log(`🔙 Fechar painel ativo: ${name}`);
        state.resetToMap();
        set({ lastOpenedPanels: { active: null } });
        return;
      }

      console.log(`🟢 Abrir painel: ${name}`);
      state.openPanel(name);
      set({ lastOpenedPanels: { active: name } });
    },

    toggleFullscreen: () => set((s) => ({ isFullscreen: !s.isFullscreen })),
    exitFullscreen: () => set({ isFullscreen: false }),

    resetToMap: () => {
      const { currentPanel, panelStack } = get();
      if (!currentPanel && panelStack.length === 0) return;

      set({ _isResetting: true });
      console.log("🗺️ Reset → mapa base");

      setTimeout(() => {
        set({
          currentPanel: null,
          panelStack: [],
          isFullscreen: false,
          _isResetting: false,
        });
      }, 50);
    },

    //------------------------------------------------------------------
    // 📍 GPS & Métricas
    //------------------------------------------------------------------
    isRecording: shouldResumeRecoveredRecording,
    isRecordingPaused: recoveredInitialPaused,
    recordingPausedAt: recoveredInitialPausedAt,
    recordingPausedDurationMs: recoveredInitialPausedDurationMs,
    recordingMode: shouldResumeRecoveredRecording
      ? recoveredRecordingDraft?.recordingMode || RECORDING_MODE_NORMAL
      : RECORDING_MODE_NORMAL,
    recordingResumePendingGap: false,
    recordingRecoveredNoticePending: shouldResumeRecoveredRecording,
    trailData:
      recoveredNavigationSession?.type === "trail"
        ? recoveredNavigationSession.trailData
        : null,
    points: shouldResumeRecoveredRecording
      ? recoveredInitialPoints
      : [],
    userPosition: null,
    trailBase:
      recoveredNavigationSession?.type === "trail"
        ? recoveredNavigationSession.trailBase || []
        : [],
    followProfilePoints:
      recoveredNavigationSession?.type === "trail"
        ? recoveredNavigationSession.trailBase || []
        : [],
    recordingStartTime: shouldResumeRecoveredRecording
      ? recoveredInitialStartTs
      : null,
    recordingSessionId: shouldResumeRecoveredRecording ? recoveredInitialSessionId : null,
    recordingFinalizeInProgress: false,

    totalDistance: shouldResumeRecoveredRecording
      ? computeRecoveredDistance(recoveredInitialPoints)
      : 0,
    movingTime: 0,
    stoppedTime: 0,
    lastUpdate: null,
    lastSpeed: 0,
    lastElevation: 0,
    elevationGain: 0,
    elevationLoss: 0,
    accuracyNow: null,

    setTrailBase: (base) => {
      const next = Array.isArray(base) ? base : [];
      set({ trailBase: next });
      if (
        next.length < 2 &&
        !get().navWaypointTarget &&
        !get().navWaypointPending
      ) {
        clearActiveNavigationPersistence();
      } else {
        persistActiveNavigation(get());
      }
    },
    setFollowProfilePoints: (points) =>
      set({ followProfilePoints: Array.isArray(points) ? points : [] }),
    consumeRecordingRecoveredNotice: () =>
      set({ recordingRecoveredNoticePending: false }),
    setIsRecording: (v) => {
      const next = !!v;
      set({
        isRecording: next,
        ...(next ? {} : { recordingMode: RECORDING_MODE_NORMAL }),
      });
      if (!next) clearRecordingDraft();
      else persistRecordingDraft(get(), { force: true });
    },
    setRecordingMode: (mode) => {
      const next =
        mode === RECORDING_MODE_EXPEDITION
          ? RECORDING_MODE_EXPEDITION
          : RECORDING_MODE_NORMAL;
      set((state) => {
        if (state.isRecording) return state;
        return { recordingMode: next };
      });
    },
    pauseRecording: () => {
      const now = Date.now();
      const current = get();
      if (current.isRecording && !current.isRecordingPaused) {
        postIOSNativeBridge("stopRecordingLocation");
      }
      set((state) => {
        if (!state.isRecording || state.isRecordingPaused) return state;
        return {
          isRecordingPaused: true,
          recordingPausedAt: now,
        };
      });
      persistRecordingDraft(get(), { force: true });
    },
    resumeRecording: () => {
      const now = Date.now();
      const current = get();
      if (current.isRecording && current.isRecordingPaused) {
        postIOSNativeBridge("startRecordingLocation", getIOSRecordingGpsConfig());
      }
      set((state) => {
        if (!state.isRecording || !state.isRecordingPaused) return state;
        const pausedAt = Number(state.recordingPausedAt || 0);
        const extraPaused = pausedAt > 0 ? Math.max(0, now - pausedAt) : 0;
        return {
          isRecordingPaused: false,
          recordingPausedAt: null,
          recordingPausedDurationMs: Number(state.recordingPausedDurationMs || 0) + extraPaused,
          recordingResumePendingGap: true,
        };
      });
      persistRecordingDraft(get(), { force: true });
    },
    setTrailData: (t) => {
      set({ trailData: t || null });
      persistActiveNavigation(get());
    },
    flushRecordingCheckpoint: () => {
      try {
        const state = get();
        if (!state?.isRecording) return;
        persistRecordingDraft(state, { force: true });
        appendRecordingJournalPoints(
          state.recordingStartTime,
          [],
          state.recordingSessionId
        );
      } catch {}
    },
    setPoints: (arr, opts = {}) => {
      set({ points: Array.isArray(arr) ? arr : [] });
      const state = get();
      if (state.isRecording && opts?.persist !== false) {
        persistRecordingDraft(state);
        appendRecordingJournalPoints(
          state.recordingStartTime,
          state.points,
          state.recordingSessionId
        );
      }
    },

    recoverRecordingFromNativeSnapshot: async () => {
      try {
        const capacitor = window?.Capacitor;
        if (!capacitor?.isNativePlatform?.()) {
          return { ok: false, reason: "not_native" };
        }
        if (capacitor.getPlatform?.() !== "android") {
          return { ok: false, reason: "not_android" };
        }
        const plugin =
          capacitor?.Plugins?.GPSNativePlugin || capacitor?.Plugins?.GPSNative;
        if (!plugin?.getRecordingStatus || !plugin?.getRecordingSnapshot) {
          return { ok: false, reason: "plugin_unavailable" };
        }

        const status = await plugin.getRecordingStatus();
        if (!status?.recording) {
          return { ok: false, reason: "not_recording" };
        }

        const res = await plugin.getRecordingSnapshot();
        const rawPoints = Array.isArray(res?.points) ? res.points : [];
        const recoveredPoints = rawPoints
          .map((p) => ({
            lat: Number(p?.lat),
            lon: Number(p?.lon),
            ele: Number.isFinite(Number(p?.ele ?? p?.alt))
              ? Number(p.ele ?? p.alt)
                : null,
            acc: Number.isFinite(Number(p?.acc)) ? Number(p.acc) : null,
            spd: Number.isFinite(Number(p?.spd ?? p?.speed))
              ? Number(p.spd ?? p.speed)
              : 0,
            spdAcc: Number.isFinite(Number(p?.spdAcc)) ? Number(p.spdAcc) : null,
            ts: Number(p?.ts || p?.time || 0),
            seq: Number.isFinite(Number(p?.seq)) ? Number(p.seq) : undefined,
            gap: !!p?.gap,
          }))
          .filter(
            (p) =>
              Number.isFinite(p.lat) &&
              Number.isFinite(p.lon) &&
              Number.isFinite(p.ts) &&
              p.ts > 0
          )
          .sort((a, b) => (a.ts || 0) - (b.ts || 0));

        const current = get();
        const currentPoints = Array.isArray(current?.points) ? current.points : [];
        const journal = readRecordingJournal();
        const journalPoints = Array.isArray(journal?.points) ? journal.points : [];
        const mergedPoints = mergeRecoveredRecordingPoints(
          recoveredPoints,
          currentPoints,
          journalPoints
        );

        if (mergedPoints.length < 2) {
          logEvent("info", "RECORDING_NATIVE_RECOVER_SKIPPED", {
            reason: "empty_snapshot",
            recoveredPoints: recoveredPoints.length,
            existingPoints: currentPoints.length,
            journalPoints: journalPoints.length,
            sessionId: status?.sessionId || res?.sessionId || null,
          });
          return { ok: false, reason: "empty_snapshot" };
        }

        if (
          current.isRecording &&
          currentPoints.length >= mergedPoints.length &&
          Number(current.recordingStartTime || 0) > 0
        ) {
          logEvent("info", "RECORDING_NATIVE_RECOVER_SKIPPED", {
            reason: "current_session_newer",
            existingPoints: currentPoints.length,
            recoveredPoints: recoveredPoints.length,
            journalPoints: journalPoints.length,
            mergedPoints: mergedPoints.length,
            sessionId: status?.sessionId || res?.sessionId || null,
          });
          return {
            ok: true,
            recovered: false,
            reason: "current_session_newer",
            points: currentPoints.length,
          };
        }

        const shouldNotifyRecovered =
          !current.isRecording || currentPoints.length < 2;
        logEvent("info", "RECORDING_NATIVE_RECOVER_DECISION", {
          shouldNotifyRecovered,
          currentRecording: !!current.isRecording,
          existingPoints: currentPoints.length,
          recoveredPoints: recoveredPoints.length,
          journalPoints: journalPoints.length,
          mergedPoints: mergedPoints.length,
          sessionId: status?.sessionId || res?.sessionId || null,
        });

        set({
          isRecording: true,
          isRecordingPaused: false,
          recordingPausedAt: null,
          recordingPausedDurationMs: 0,
          recordingMode: current.recordingMode || RECORDING_MODE_NORMAL,
          recordingResumePendingGap: false,
          recordingRecoveredNoticePending: shouldNotifyRecovered,
          recordingStartTime: Number(
            current.recordingStartTime ||
              mergedPoints[0]?.ts ||
              recoveredPoints[0]?.ts ||
              Date.now()
          ),
          recordingStopTime: null,
          points: mergedPoints,
          totalDistance: computeRecoveredDistance(mergedPoints),
          movingTime: 0,
          stoppedTime: 0,
          elevationGain: 0,
          elevationLoss: 0,
        });
        persistRecordingDraft(get(), { force: true });
        appendRecordingJournalPoints(get().recordingStartTime, mergedPoints);
        logEvent("warn", "RECORDING_NATIVE_RECOVERED", {
          points: mergedPoints.length,
          recoveredPoints: recoveredPoints.length,
          existingPoints: currentPoints.length,
          sessionId: status?.sessionId || res?.sessionId || null,
          hasFile: !!status?.hasFile,
        });
        return {
          ok: true,
          recovered: true,
          points: mergedPoints.length,
          sessionId: status?.sessionId || res?.sessionId || null,
        };
      } catch (err) {
        logEvent("warn", "RECORDING_NATIVE_RECOVER_FAIL", {
          err: err?.message || String(err),
        });
        return {
          ok: false,
          reason: "exception",
          err: err?.message || String(err),
        };
      }
    },

    setUserPosition: (pos) => {
      const state = get();
      if (typeof pos?.lat !== "number" || typeof pos?.lon !== "number") return;

      const now = Date.now();
      let newDistance = state.totalDistance;
      let newMoving = state.movingTime;
      let newStopped = state.stoppedTime;

      if (state.lastUpdate && state.userPosition) {
        const dt = (now - state.lastUpdate) / 1000;
        const dist = haversine(
          { lat: state.userPosition.lat, lon: state.userPosition.lon },
          { lat: pos.lat, lon: pos.lon }
        );

        if (dist > 1) {
          newDistance += dist;
          newMoving += dt;
        } else {
          newStopped += dt;
        }
      }

      set({
        userPosition: pos,
        lastUpdate: now,
        totalDistance: newDistance,
        movingTime: newMoving,
        stoppedTime: newStopped,
        lastSpeed: pos.spd ?? pos.speed ?? 0,
        lastElevation: pos.ele ?? pos.alt ?? null,
        accuracyNow: pos.acc ?? null,
      });
      persistLastKnownPosition(pos, now);
    },

    addPoint: (point) => {
      let shouldPersist = false;
      let justAdded = null;
      set((state) => {
        if (
          !point ||
          typeof point.lat !== "number" ||
          typeof point.lon !== "number"
        )
          return state;

        const newPoint = {
          lat: point.lat,
          lon: point.lon,
          ele: typeof point.ele === "number" ? point.ele : null,
          acc: typeof point.acc === "number" ? point.acc : null,
          spd: typeof point.spd === "number" ? point.spd : 0,
          ts: point.ts || Date.now(),
          seq: typeof point.seq === "number" ? point.seq : undefined,
          gap: !!point.gap,
        };
        if (state.recordingResumePendingGap && !newPoint.gap) {
          newPoint.gap = true;
        }

        // No minimum step filter: keep all points for smoother Android rendering.

        const updated = [...state.points, newPoint];
        shouldPersist = !!state.isRecording;
        justAdded = newPoint;
        return { points: updated, recordingResumePendingGap: false };
      });
      if (shouldPersist) {
        const state = get();
        persistRecordingDraft(state);
        appendRecordingJournalPoints(state.recordingStartTime, justAdded ? [justAdded] : []);
      }
    },

    appendPoints: (pointsBatch, options = {}) => {
      let shouldPersist = false;
      let mappedForJournal = [];
      set((state) => {
        if (!Array.isArray(pointsBatch) || pointsBatch.length === 0) return state;

        const mapped = [];
        for (let i = 0; i < pointsBatch.length; i += 1) {
          const point = pointsBatch[i];
          if (
            !point ||
            typeof point.lat !== "number" ||
            typeof point.lon !== "number"
          ) {
            continue;
          }
          mapped.push({
            lat: point.lat,
            lon: point.lon,
            ele: typeof point.ele === "number" ? point.ele : null,
            acc: typeof point.acc === "number" ? point.acc : null,
            spd: typeof point.spd === "number" ? point.spd : 0,
            spdAcc: typeof point.spdAcc === "number" ? point.spdAcc : null,
            ts: point.ts || Date.now(),
            seq: typeof point.seq === "number" ? point.seq : undefined,
            gap: !!point.gap,
          });
        }

        if (!mapped.length) return state;
        if (state.recordingResumePendingGap && mapped[0] && !mapped[0].gap) {
          mapped[0] = { ...mapped[0], gap: true };
        }
        mappedForJournal = mapped;
        shouldPersist = !!state.isRecording && options.persist !== false;
        return {
          points: [...state.points, ...mapped],
          recordingResumePendingGap: false,
        };
      });

      if (shouldPersist) {
        const state = get();
        persistRecordingDraft(state);
        appendRecordingJournalPoints(
          state.recordingStartTime,
          mappedForJournal,
          state.recordingSessionId
        );
      }
    },

    startRecording: (opts = {}) => {
      const startedAt = Date.now();
      const sessionId = `rec-${startedAt}`;
      const recordingMode =
        opts?.mode === RECORDING_MODE_EXPEDITION
          ? RECORDING_MODE_EXPEDITION
          : RECORDING_MODE_NORMAL;
      postIOSNativeBridge("startRecordingLocation", getIOSRecordingGpsConfig());

      set(() => ({
        isRecording: true,
        isRecordingPaused: false,
        recordingPausedAt: null,
        recordingPausedDurationMs: 0,
        recordingMode,
        recordingResumePendingGap: false,
        recordingStartTime: startedAt,
        recordingSessionId: sessionId,
        recordingFinalizeInProgress: false,
        recordingStopTime: null,
        points: [],
        totalDistance: 0,
        movingTime: 0,
        stoppedTime: 0,
        elevationGain: 0,
        elevationLoss: 0,
      }));

      // Marcar primeiro a gravação como ativa impede que o cleanup de uma
      // instância anterior do mapa pare o serviço acabado de iniciar.
      try {
        const plugin =
          window?.Capacitor?.Plugins?.GPSNativePlugin ||
          window?.Capacitor?.Plugins?.GPSNative;
        if (plugin?.startTracking) {
          logEvent("system", "Android recording: ensure BG start", {
            recordingStartTime: startedAt,
          });
          Promise.resolve(
            plugin.startTracking({
              intervalMs: 2000,
              fastestMs: 1000,
              minDistanceM: 3,
              highAccuracy: true,
            })
          ).catch((error) => {
            logEvent("warn", "Android recording: BG start failed", {
              recordingStartTime: startedAt,
              error: error?.message || String(error),
            });
          });
        }
      } catch (error) {
        logEvent("warn", "Android recording: BG start failed", {
          recordingStartTime: startedAt,
          error: error?.message || String(error),
        });
      }

      clearRecordingJournal();
      appendRecordingJournalPoints(startedAt, [], sessionId);
      persistRecordingDraft(get(), { force: true });
    },

    stopRecording: () => {
      postIOSNativeBridge("stopRecordingLocation");
      clearRecordingDraft();
      clearRecordingJournal();
      return set((state) => ({
        isRecording: false,
        isRecordingPaused: false,
        recordingPausedAt: null,
        recordingPausedDurationMs: 0,
        recordingMode: RECORDING_MODE_NORMAL,
        recordingResumePendingGap: false,
        recordingSessionId: null,
        recordingFinalizeInProgress: false,
        recordingStopTime: state.recordingStartTime ? Date.now() : null,
      }));
    },

    //------------------------------------------------------------------
    // 📘 HISTÓRICO OFICIAL (FINAL) — ANDROID + IOS
    //------------------------------------------------------------------
    saveTrackToHistory: async () => {
      try {
        const s = get();
        const authState = useAuthStore.getState();
        const user = authState?.user || null;
        const stableSessionId =
          s?.recordingSessionId ||
          `rec-${Number(s?.recordingStartTime || Date.now())}`;

        const journal = readRecordingJournal();
        const draft = readRecordingDraft();
        const mergedSourcePoints = mergeRecoveredRecordingPoints(
          s.points,
          draft?.points || [],
          journal?.points || []
        );
        const sourcePointCount = Array.isArray(mergedSourcePoints)
          ? mergedSourcePoints.length
          : 0;
        let pts = sanitizeTrackPoints(mergedSourcePoints);
        const isAndroid =
          typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent);
        const isIOSNative = isIOSNativeContainer();
        if (
          isAndroid &&
          pts.length < 2 &&
          Array.isArray(s.points) &&
          s.points.length >= 2
        ) {
          pts = s.points;
        }
        pts = prepareRecordingMetricPoints(pts, { android: isAndroid });
        const heartRateState = useHeartRateStore.getState();
        const heartRateValues = (heartRateState.samples || [])
          .map((sample) => Number(sample?.bpm))
          .filter(Number.isFinite);
        const heartRate = heartRateValues.length
          ? {
              sampleCount: heartRateValues.length,
              averageBpm: Math.round(
                heartRateValues.reduce((sum, value) => sum + value, 0) /
                  heartRateValues.length
              ),
              minBpm: Math.min(...heartRateValues),
              maxBpm: Math.max(...heartRateValues),
              source: heartRateState.source || null,
              deviceName: heartRateState.deviceName || null,
              samples: [...heartRateState.samples],
            }
          : null;
        const canSaveHeartRateOnly = !!s.recordingStartTime && heartRateValues.length >= 2;
        if ((pts.length < 2 && !canSaveHeartRateOnly) || !s.recordingStartTime) {
          console.log("⚠️ Nada para guardar no histórico.");
          logEvent("warn", "HISTORY_SAVE_SKIP", {
            points: pts.length,
            hasStart: !!s.recordingStartTime,
            heartRateSampleCount: heartRateValues.length,
          });
          return { ok: false, reason: "empty", points: pts };
        }
        if (pts.length < 2 && canSaveHeartRateOnly) {
          logEvent("warn", "HISTORY_HEART_RATE_ONLY_FALLBACK", {
            entryId: stableSessionId,
            points: pts.length,
            heartRateSampleCount: heartRateValues.length,
            reason: "gps_track_incomplete",
          });
        }

        const statsFromPoints = calculateRecordingMetrics(pts, {
          startedAt: s.recordingStartTime,
          stoppedAt: s.recordingStopTime || Date.now(),
          pausedMilliseconds: s.recordingPausedDurationMs,
        });
        const dist = statsFromPoints.distanceKm;
        const moving = statsFromPoints.movingSeconds;
        const stopped = statsFromPoints.stoppedSeconds;
        const dPlus = statsFromPoints.elevationGain;
        const avgMovingKmh = dist > 0 && moving > 0 ? dist / (moving / 3600) : 0;
        const gamificationEligible = isAverageSpeedValidForGamification(
          avgMovingKmh,
          MAX_GAMIFICATION_AVG_SPEED_KMH
        );

        const recordingMode =
          s.recordingMode === RECORDING_MODE_EXPEDITION
            ? RECORDING_MODE_EXPEDITION
            : RECORDING_MODE_NORMAL;
        const activityMode = normalizeActivityMode(
          useActivityModeStore.getState()?.activityMode,
          ACTIVITY_MODE_WALK
        );
        const calorieEstimate = estimateActiveCalories({
          weightKg: user?.weightKg,
          dateOfBirth: user?.dob,
          sex: user?.sex || user?.gender,
          activityMode,
          movingMinutes: moving / 60,
          totalMinutes: (moving + stopped) / 60,
          speedKmh: avgMovingKmh,
          elevationGainM: dPlus,
          distanceKm: dist,
          heartRateSamples: heartRate?.samples,
          recordingStartedAt: Number(s.recordingStartTime) || null,
          recordingStoppedAt: Number(s.recordingStopTime || Date.now()) || null,
        });
        const name = formatHistoryDefaultName(s.recordingStartTime);
        logEvent(heartRate ? "info" : "warn", "HISTORY_HEART_RATE_CAPTURE", {
          entryId: stableSessionId,
          sampleCount: heartRate?.sampleCount || 0,
          averageBpm: heartRate?.averageBpm || null,
          source: heartRate?.source || heartRateState.source || null,
          status: heartRateState.status,
          diagnostics: heartRateState.getDiagnostics?.() || null,
        });

        const buildEntry = (
          pointsList,
          nativeRef = null,
          pointsTotal = null,
          options = {}
        ) => ({
          id: stableSessionId,
          sessionId: stableSessionId,
          uid: user?.uid || null,
          name,
          date: formatHistoryDefaultName(Date.now()),
          mode: recordingMode,
          activityMode,
          recordingPlatform: isAndroid ? "android" : isIOSNative ? "ios" : "web",
          recordingStartedAt: Number(s.recordingStartTime) || null,
          recordingStoppedAt: Number(s.recordingStopTime || Date.now()) || null,
          recordingPausedDurationMs:
            Number(s.recordingPausedDurationMs || 0) || 0,
          heartRate,
          caloriesKcal: calorieEstimate?.activeKcal ?? null,
          points: pointsList,
          gpx:
            typeof options.gpx === "string" && options.gpx.trim()
              ? options.gpx
              : undefined,
          native: nativeRef ? { gpx: nativeRef } : undefined,
          gamification: {
            eligible: gamificationEligible,
            reason: gamificationEligible ? null : "avg_speed_too_high",
            avgSpeedKmh: Number(avgMovingKmh.toFixed(1)),
            maxAvgSpeedKmh: MAX_GAMIFICATION_AVG_SPEED_KMH,
          },
          stats: {
            distKm: Number(dist.toFixed(2)),
            totalMin: Number(((moving + stopped) / 60).toFixed(1)),
            movingMin: Number((moving / 60).toFixed(1)),
            stoppedMin: Number((stopped / 60).toFixed(1)),
            speed: Number(
              dist > 0 && moving > 0 ? (dist / (moving / 3600)).toFixed(1) : 0
            ),
            dPlus: Math.round(dPlus),
            caloriesKcal: calorieEstimate?.activeKcal ?? null,
            elevationSource: isAndroid ? "terrain" : "gps",
            points: Number(
              Math.max(
                Number(pointsTotal ?? 0),
                pointsList.length,
                pts.length,
                sourcePointCount
              )
            ),
            savedPoints: pointsList.length,
            sanitizedPoints: pts.length,
            sourcePoints: sourcePointCount,
            calculationVersion: 4,
            partialRecording: pts.length < 2,
            partialReason: pts.length < 2 ? "gps_track_incomplete" : null,
          },
        });

        const downsamplePoints = (list, maxPoints) => {
          if (!Array.isArray(list) || list.length <= maxPoints) return list;
          const target = Math.max(2, Math.floor(Number(maxPoints) || 2));
          const lastIndex = list.length - 1;
          const sampled = [];
          let previousIndex = -1;
          for (let i = 0; i < target; i += 1) {
            const index = Math.round((i * lastIndex) / (target - 1));
            if (index === previousIndex) continue;
            sampled.push(list[index]);
            previousIndex = index;
          }
          const last = list[list.length - 1];
          if (sampled[sampled.length - 1] !== last) sampled.push(last);
          return sampled;
        };

        const loadHistory = () => {
          try {
            const raw = localStorage.getItem("historygps") || "[]";
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [];
          } catch {
            return [];
          }
        };

        const trySaveWithPrune = (entry) => {
          const nativeStorage = isNativePlatform();
          const compactEntryForLocalStorage = (item, index = 0) => {
            if (!item || typeof item !== "object") return item;
            const cloudConfirmed =
              item?.cloud?.synced === true ||
              (String(item?.cloud?.file || "").trim() &&
                String(item?.cloud?.url || "").trim());
            const pointLimit = nativeStorage
              ? index < 8
                ? 700
                : 180
              : index < 20
              ? 1600
              : 400;
            const points = cloudConfirmed
              ? []
              : Array.isArray(item.points)
                ? downsamplePoints(item.points, pointLimit)
                : [];
            const next = {
              ...item,
              points,
              previewPoints: Math.min(
                Number(item.previewPoints || item.savedPoints || points.length || 0),
                points.length
              ),
            };
            if (nativeStorage) {
              delete next.gpx;
            }
            const hrSamples = Array.isArray(next?.heartRate?.samples)
              ? next.heartRate.samples
              : [];
            if (cloudConfirmed && hrSamples.length) {
              next.heartRate = {
                ...next.heartRate,
                samples: [],
                storedSampleCount: 0,
                samplesAvailableInGpx: true,
              };
              return next;
            }
            const heartRateLimit = nativeStorage ? 2400 : 4800;
            if (hrSamples.length > heartRateLimit) {
              next.heartRate = {
                ...next.heartRate,
                samples: downsamplePoints(hrSamples, heartRateLimit),
                storedSampleCount: heartRateLimit,
              };
            }
            return next;
          };
          let history = loadHistory();
          history = history.filter((h) => h?.id !== entry?.id);
          history = history
            .slice(0, nativeStorage ? 80 : 160)
            .map((h, idx) => compactEntryForLocalStorage(h, idx + 1));
          const compactEntry = compactEntryForLocalStorage(entry, 0);
          for (let i = 0; i <= history.length; i += 1) {
            try {
              localStorage.setItem(
                "historygps",
                JSON.stringify([compactEntry, ...history])
              );
              window.dispatchEvent(new CustomEvent("trekkin:history-updated"));
              const persisted = loadHistory().find((item) => item?.id === entry?.id);
              const persistedHeartRate = persisted?.heartRate || null;
              logEvent(persistedHeartRate ? "info" : "warn", "HISTORY_HEART_RATE_PERSISTED", {
                entryId: entry?.id || null,
                capturedSampleCount: entry?.heartRate?.sampleCount || 0,
                storedSampleCount: persistedHeartRate?.samples?.length || 0,
                hasHeartRate: !!persistedHeartRate,
                storageMode: nativeStorage ? "native-localStorage" : "web-localStorage",
              });
              return true;
            } catch (err) {
              console.warn("⚠️ Falha ao guardar histórico:", err);
              logEvent("warn", "HISTORY_SAVE_RETRY", {
                attempt: i + 1,
                historyLen: history.length,
                points: entry?.points?.length,
                err: err?.message || String(err),
              });
              history = history.slice(0, Math.max(0, history.length - 1));
            }
          }
          return false;
        };

        const fullGpxText = buildHistoryGpx(pts, name, heartRate);
        let nativeRef = null;
        if (isNativePlatform()) {
          try {
            if (fullGpxText) {
              nativeRef = await writeHistoryGpxNative(fullGpxText, name);
            }
          } catch {}
        }

        const isNative = isNativePlatform();
        const storageMax = isNative ? 900 : 4000;
        const previewMax = isNative ? 500 : 1200;
        const storagePoints =
          pts.length > storageMax ? downsamplePoints(pts, storageMax) : pts;
        const shouldKeepGpxForUpload =
          !nativeRef &&
          typeof fullGpxText === "string" &&
          fullGpxText.trim().length > 0 &&
          (pts.length > storagePoints.length || pts.length > 1500);

        let entry = buildEntry(storagePoints, nativeRef, pts.length, {
          gpx: shouldKeepGpxForUpload ? fullGpxText : undefined,
        });
        if (!trySaveWithPrune(entry)) {
          const reduced = downsamplePoints(pts, previewMax);
          entry = buildEntry(reduced, nativeRef, pts.length, {
            gpx: shouldKeepGpxForUpload ? fullGpxText : undefined,
          });
          if (!trySaveWithPrune(entry)) {
            const reducedMore = downsamplePoints(pts, 400);
            entry = buildEntry(reducedMore, nativeRef, pts.length, {
              gpx: shouldKeepGpxForUpload ? fullGpxText : undefined,
            });
            if (!trySaveWithPrune(entry)) {
              if (shouldKeepGpxForUpload) {
                const gpxOnlyEntry = {
                  ...buildEntry([], nativeRef, pts.length, { gpx: fullGpxText }),
                  lite: true,
                  localGpxOnly: true,
                };
                if (trySaveWithPrune(gpxOnlyEntry)) {
                  logEvent("warn", "HISTORY_SAVE_GPX_ONLY", {
                    id: gpxOnlyEntry.id,
                    points: pts.length,
                    gpxBytes: fullGpxText.length,
                  });
                  logEvent("info", "HISTORY_SAVE_OK", {
                    id: gpxOnlyEntry.id,
                    points: 0,
                    savedPoints: 0,
                    sanitizedPoints: pts.length,
                    sourcePoints: sourcePointCount,
                    distKm: gpxOnlyEntry.stats?.distKm,
                    storageMode: "gpx-only",
                  });
                  return {
                    ok: true,
                    entryId: gpxOnlyEntry.id,
                    distKm: Number(dist.toFixed(2)),
                    storageMode: "gpx-only",
                    gamification: {
                      eligible: gamificationEligible,
                      reason: gamificationEligible ? null : "avg_speed_too_high",
                      avgSpeedKmh: Number(avgMovingKmh.toFixed(1)),
                      maxAvgSpeedKmh: MAX_GAMIFICATION_AVG_SPEED_KMH,
                    },
                  };
                }
              }
              if (nativeRef) {
                const nativeLiteEntry = {
                  ...buildEntry([], nativeRef, pts.length),
                  lite: true,
                  localOnlyNative: true,
                };
                if (trySaveWithPrune(nativeLiteEntry)) {
                  logEvent("warn", "HISTORY_SAVE_NATIVE_LITE", {
                    id: nativeLiteEntry.id,
                    points: pts.length,
                  });
                  console.warn("⚠️ Histórico guardado em modo lite com GPX nativo.");
                return {
                  ok: true,
                  entryId: nativeLiteEntry.id,
                  distKm: Number(dist.toFixed(2)),
                  storageMode: "native-lite",
                  gamification: {
                    eligible: gamificationEligible,
                      reason: gamificationEligible ? null : "avg_speed_too_high",
                      avgSpeedKmh: Number(avgMovingKmh.toFixed(1)),
                      maxAvgSpeedKmh: MAX_GAMIFICATION_AVG_SPEED_KMH,
                    },
                  };
                }
              }
              const emergencyLiteEntry = {
                ...buildEntry([], nativeRef, pts.length),
                lite: true,
                localEmergency: true,
                localOnlyNative: !!nativeRef,
              };
              if (trySaveWithPrune(emergencyLiteEntry)) {
                logEvent("warn", "HISTORY_SAVE_EMERGENCY_LITE", {
                  id: emergencyLiteEntry.id,
                  points: pts.length,
                  hasNativeRef: !!nativeRef,
                });
                console.warn("⚠️ Histórico guardado em modo emergência (offline/local).");
                return {
                  ok: true,
                  entryId: emergencyLiteEntry.id,
                  distKm: Number(dist.toFixed(2)),
                  storageMode: "emergency-lite",
                  requiresCloudBackup: true,
                  points: pts,
                  stats: statsFromPoints,
                  name,
                  gamification: {
                    eligible: gamificationEligible,
                    reason: gamificationEligible ? null : "avg_speed_too_high",
                    avgSpeedKmh: Number(avgMovingKmh.toFixed(1)),
                    maxAvgSpeedKmh: MAX_GAMIFICATION_AVG_SPEED_KMH,
                  },
                };
              }
              console.error("❌ Histórico não guardado (quota/localStorage).");
              logEvent("error", "HISTORY_SAVE_FAIL", {
                points: pts.length,
                reduced6000: reduced.length,
                reduced2000: reducedMore.length,
              });
              return {
                ok: false,
                reason: "quota",
                entryId: entry?.id || `rec-${Number(get().recordingStartTime || Date.now())}`,
                distKm: Number(dist.toFixed(2)),
                points: pts,
                stats: statsFromPoints,
                name,
                gamification: {
                  eligible: gamificationEligible,
                  reason: gamificationEligible ? null : "avg_speed_too_high",
                  avgSpeedKmh: Number(avgMovingKmh.toFixed(1)),
                  maxAvgSpeedKmh: MAX_GAMIFICATION_AVG_SPEED_KMH,
                },
              };
            }
          }
        }

        console.log("💾 Histórico gravado com sucesso:", entry.id);
        logEvent("info", "HISTORY_SAVE_OK", {
          id: entry.id,
          points: entry.points?.length,
          savedPoints: entry.stats?.savedPoints,
          sanitizedPoints: entry.stats?.sanitizedPoints,
          sourcePoints: entry.stats?.sourcePoints,
          distKm: entry.stats?.distKm,
          dPlus: entry.stats?.dPlus,
          heartRateSampleCount: entry.heartRate?.sampleCount || 0,
          storedHeartRateSamples: entry.heartRate?.samples?.length || 0,
        });

        return {
          ok: true,
          entryId: entry.id,
          distKm: Number(dist.toFixed(2)),
          dPlus: Math.round(dPlus),
          recordedAt: Number(s.recordingStartTime) || Date.now(),
          gamification: {
            eligible: gamificationEligible,
            reason: gamificationEligible ? null : "avg_speed_too_high",
            avgSpeedKmh: Number(avgMovingKmh.toFixed(1)),
            maxAvgSpeedKmh: MAX_GAMIFICATION_AVG_SPEED_KMH,
          },
        };
      } catch (err) {
        console.error("❌ Erro a guardar histórico:", err);
        logEvent("error", "HISTORY_SAVE_ERROR", {
          err: err?.message || String(err),
        });
        return { ok: false, reason: "exception" };
      }
    },

    applyRecordingDistanceToMonthlyRanking: async ({
      entryId,
      distKm,
      dPlus = 0,
      activityMode: requestedMode,
      recordedAt,
    } = {}) => {
      const user = useAuthStore.getState()?.user;
      const uid = String(user?.uid || "");
      const distance = Number(distKm || 0);
      const elevationGainM = Math.max(0, Math.round(Number(dPlus || 0)));
      if (!uid || !(distance > 0) || !entryId) return { applied: false };

      const mode = normalizeActivityMode(
        requestedMode || useActivityModeStore.getState()?.activityMode,
        ACTIVITY_MODE_WALK
      );
      const date = new Date(Number(recordedAt || Date.now()));
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      const contributionId = String(entryId)
        .replace(/[.#$\[\]/]/g, "_")
        .slice(0, 120);
      let applied = false;

      const tx = await runTransaction(
        ref(db, `gamification/users/${uid}`),
        (currentValue) => {
          const current = currentValue && typeof currentValue === "object"
            ? { ...currentValue }
            : {};
          const contributions = current.recordingContributions &&
            typeof current.recordingContributions === "object"
            ? { ...current.recordingContributions }
            : {};
          if (contributions[contributionId]) return current;

          const roundedDistance = Number(distance.toFixed(2));
          const nowTs = Date.now();
          contributions[contributionId] = {
            distanceKm: roundedDistance,
            elevationGainM,
            activityMode: mode,
            monthKey,
            appliedAt: nowTs,
          };
          current.recordingContributions = contributions;

          const byMode = { ...(current.monthlyStatsByMode || {}) };
          const modeMonths = { ...(byMode[mode] || {}) };
          const modeStats = { ...(modeMonths[monthKey] || {}) };
          modeMonths[monthKey] = {
            ...modeStats,
            monthKey,
            activityMode: mode,
            distanceKm: Number((Number(modeStats.distanceKm || 0) + roundedDistance).toFixed(2)),
            elevationGainM: Math.round(Number(modeStats.elevationGainM || 0) + elevationGainM),
            updatedAt: nowTs,
          };
          byMode[mode] = modeMonths;
          current.monthlyStatsByMode = byMode;

          if (mode === ACTIVITY_MODE_WALK) {
            const monthlyStats = { ...(current.monthlyStats || {}) };
            const monthStats = { ...(monthlyStats[monthKey] || {}) };
            monthlyStats[monthKey] = {
              ...monthStats,
              monthKey,
              distanceKm: Number((Number(monthStats.distanceKm || 0) + roundedDistance).toFixed(2)),
              elevationGainM: Math.round(Number(monthStats.elevationGainM || 0) + elevationGainM),
              updatedAt: nowTs,
            };
            current.monthlyStats = monthlyStats;
          }
          current.updatedAt = nowTs;
          applied = true;
          return current;
        }
      );
      return { applied: tx.committed && applied, monthKey, activityMode: mode };
    },

    syncMonthlyRankingKmFromHistory: async () => {
      const auth = useAuthStore.getState();
      const user = auth?.user;
      if (!user?.uid) return;
      try {
        const nowTs = Date.now();
        const nowDate = new Date(nowTs);
        const monthStartTs = new Date(
          nowDate.getFullYear(),
          nowDate.getMonth(),
          1
        ).getTime();
        const monthKey = `${nowDate.getFullYear()}-${String(
          nowDate.getMonth() + 1
        ).padStart(2, "0")}`;

        const safeNum = (value, fallback = 0) => {
          const n = Number(value);
          return Number.isFinite(n) ? n : fallback;
        };

        const parseTs = (value) => {
          if (typeof value === "number" && Number.isFinite(value) && value > 0) {
            return value < 1e12 ? value * 1000 : value;
          }
          if (typeof value === "string") {
            const raw = value.trim();
            if (!raw) return 0;
            const asNum = Number(raw);
            if (Number.isFinite(asNum) && asNum > 0) {
              return asNum < 1e12 ? asNum * 1000 : asNum;
            }
            const parsed = Date.parse(raw);
            if (Number.isFinite(parsed) && parsed > 0) return parsed;
          }
          return 0;
        };

        const resolveEntryTs = (entry) => {
          if (!entry || typeof entry !== "object") return 0;
          const direct =
            parseTs(entry.ts) ||
            parseTs(entry.time) ||
            parseTs(entry.createdAt) ||
            parseTs(entry.updatedAt) ||
            parseTs(entry.date) ||
            parseTs(entry?.cloud?.mtimeTs) ||
            parseTs(entry?.cloud?.mtime) ||
            parseTs(entry?.cloud?.syncedAt);
          if (direct) return direct;
          const id = String(entry.id || "");
          const m = id.match(/rec-(\d{10,13})/i);
          if (m && m[1]) return parseTs(m[1]);
          const cloudFile = String(entry?.cloud?.file || "");
          const fm = cloudFile.match(/(\d{10,13})/);
          if (fm && fm[1]) return parseTs(fm[1]);
          return 0;
        };

        const resolveDistKm = (entry) =>
          parseDistanceKmValue(
            entry?.stats?.distKm ??
              entry?.distanceKm ??
              entry?.distKm ??
              entry?.stats?.distanceKm ??
              entry?.dist,
            0
          );

        const resolveAvgKmh = (entry, distKm) => {
          const fromStats = safeNum(
            entry?.stats?.speed ?? entry?.avgSpeedKmh ?? entry?.speedKmh,
            NaN
          );
          if (Number.isFinite(fromStats) && fromStats >= 0) return fromStats;
          const movingMin = safeNum(
            entry?.stats?.movingMin ?? entry?.movingMin,
            0
          );
          const totalMin = safeNum(entry?.stats?.totalMin ?? entry?.totalMin, 0);
          const mins = movingMin > 0 ? movingMin : totalMin;
          if (distKm > 0 && mins > 0) return distKm / (mins / 60);
          return NaN;
        };

        const uid = String(user.uid || "");
        const activityMode = normalizeActivityMode(
          useActivityModeStore.getState()?.activityMode,
          ACTIVITY_MODE_WALK
        );
        const rawHistory = localStorage.getItem("historygps") || "[]";
        const parsedHistory = JSON.parse(rawHistory);
        const historyList = Array.isArray(parsedHistory) ? parsedHistory : [];

        let monthlyKm = 0;
        let hasOwnedHistoryThisMonth = false;
        historyList.forEach((entry) => {
          const ts = resolveEntryTs(entry);
          if (!ts || ts < monthStartTs) return;
          if (!isHistoryEntryOwnedByUser(entry, uid, { includeUnowned: false })) return;
          if (!isActivityModeCompatible(entry?.activityMode, activityMode)) return;
          hasOwnedHistoryThisMonth = true;

          if (entry?.gamification?.exclude === true) return;
          if (entry?.validation?.allowGamification === false) return;

          const distKm = resolveDistKm(entry);
          if (!(distKm > 0)) return;

          if (entry?.gamification?.eligible === false) return;

          if (entry?.gamification?.eligible !== true) {
            const avgKmh = resolveAvgKmh(entry, distKm);
            const valid = isAverageSpeedValidForGamification(
              avgKmh,
              MAX_GAMIFICATION_AVG_SPEED_KMH
            );
            if (!valid) return;
          }

          monthlyKm += distKm;
        });

        const roundedMonthlyKm = Number(monthlyKm.toFixed(2));
        const privateMonthlyPath =
          activityMode === ACTIVITY_MODE_WALK
            ? `gamification/users/${uid}/monthlyStats/${monthKey}/distanceKm`
            : `gamification/users/${uid}/monthlyStatsByMode/${activityMode}/${monthKey}/distanceKm`;
        const publicMonthlyPath =
          activityMode === ACTIVITY_MODE_WALK
            ? `usersPublic/${uid}/gamification/monthlyStats/${monthKey}/distanceKm`
            : `usersPublic/${uid}/gamification/monthlyStatsByMode/${activityMode}/${monthKey}/distanceKm`;

        const readExistingDistance = async (path) => {
          try {
            const snap = await getDb(ref(db, path));
            const n = Number(snap?.val?.());
            return Number.isFinite(n) ? n : 0;
          } catch {
            return 0;
          }
        };

        const existingPrivateKm = await readExistingDistance(privateMonthlyPath);
        const existingPublicKm = await readExistingDistance(publicMonthlyPath);
        const mergedMonthlyKm = Number(
          Math.max(
            0,
            roundedMonthlyKm,
            existingPrivateKm,
            existingPublicKm
          ).toFixed(2)
        );

        const privateUpdate = {
          [`monthlyStatsByMode/${activityMode}/${monthKey}/monthKey`]: monthKey,
          [`monthlyStatsByMode/${activityMode}/${monthKey}/activityMode`]:
            activityMode,
          [`monthlyStatsByMode/${activityMode}/${monthKey}/distanceKm`]:
            mergedMonthlyKm,
          [`monthlyStatsByMode/${activityMode}/${monthKey}/updatedAt`]: nowTs,
          updatedAt: nowTs,
        };
        if (activityMode === ACTIVITY_MODE_WALK) {
          privateUpdate[`monthlyStats/${monthKey}/monthKey`] = monthKey;
          privateUpdate[`monthlyStats/${monthKey}/distanceKm`] = mergedMonthlyKm;
          privateUpdate[`monthlyStats/${monthKey}/updatedAt`] = nowTs;
        }

        const publicUpdate = {
          [`monthlyStatsByMode/${activityMode}/${monthKey}/monthKey`]: monthKey,
          [`monthlyStatsByMode/${activityMode}/${monthKey}/activityMode`]: activityMode,
          [`monthlyStatsByMode/${activityMode}/${monthKey}/distanceKm`]: mergedMonthlyKm,
          [`monthlyStatsByMode/${activityMode}/${monthKey}/updatedAt`]: nowTs,
          updatedAt: nowTs,
        };
        if (activityMode === ACTIVITY_MODE_WALK) {
          publicUpdate[`monthlyStats/${monthKey}/monthKey`] = monthKey;
          publicUpdate[`monthlyStats/${monthKey}/distanceKm`] = mergedMonthlyKm;
          publicUpdate[`monthlyStats/${monthKey}/updatedAt`] = nowTs;
        }

        const rootUpdate = {};
        Object.entries(privateUpdate).forEach(([path, value]) => {
          rootUpdate[`gamification/users/${uid}/${path}`] = value;
        });
        Object.entries(publicUpdate).forEach(([path, value]) => {
          rootUpdate[`usersPublic/${uid}/gamification/${path}`] = value;
        });
        await updateDb(ref(db), rootUpdate);
      } catch (err) {
        console.warn("⚠️ Falha ao sincronizar kms mensais do ranking:", err);
      }
    },

    //------------------------------------------------------------------
    // 🚦 STOP FINAL — SAVE + SYNC IMEDIATO (DEBUG iOS)
    //------------------------------------------------------------------
    stopAndFinalizeTrack: async () => {
      if (get().recordingFinalizeInProgress) {
        logger.warn("HISTORY_SYNC", "Finalize já em curso; ignorado");
        return false;
      }
      set({ recordingFinalizeInProgress: true });
      try {
      const capacitor = window?.Capacitor;
      const importedNative = !!Capacitor?.isNativePlatform?.();
      const windowNative = !!capacitor?.isNativePlatform?.();
      const isNative = importedNative || windowNative;
      const platform = isNative
        ? Capacitor?.getPlatform?.() || capacitor?.getPlatform?.()
        : null;
      const isAndroidNative = platform === "android";
      const isIOSNative = platform === "ios" || isIOSNativeContainer();
      const iosNetworkOptionalMode = isIOSNative;
      const nativePlugin =
        capacitor?.Plugins?.GPSNativePlugin || capacitor?.Plugins?.GPSNative;
      logEvent("info", "HISTORY_FINALIZE_START", {
        platform: isAndroidNative ? "android" : isIOSNative ? "ios" : "web",
        pointsNow: Array.isArray(get().points) ? get().points.length : 0,
        hasRecordingStart: !!get().recordingStartTime,
        isRecording: !!get().isRecording,
        online: isProbablyOnline(),
      });

      if (
        isAndroidNative &&
        typeof window?.__TREKKIN_ANDROID_FLUSH_RECORDING_POINTS === "function"
      ) {
        try {
          await withTimeout(
            window.__TREKKIN_ANDROID_FLUSH_RECORDING_POINTS("finalize"),
            2500,
            "android_finalize_flush_timeout"
          );
          logEvent("info", "ANDROID_FINALIZE_FLUSH_OK", {
            pointsNow: Array.isArray(get().points) ? get().points.length : 0,
          });
        } catch (e) {
          logEvent("warn", "ANDROID_FINALIZE_FLUSH_FAIL", {
            err: e?.message || String(e),
            pointsNow: Array.isArray(get().points) ? get().points.length : 0,
          });
        }
      }

      // Freeze the native recording before reading its final snapshot. Without
      // this, a last location callback may be appended after the snapshot was
      // read, causing the summary/ranking to be calculated from an incomplete
      // point set even though the native GPX is complete.
      if (isAndroidNative) {
        try {
          await nativePlugin?.setRecording?.({ recording: false });
          logEvent("info", "ANDROID_FINALIZE_RECORDING_FROZEN", {
            pointsNow: Array.isArray(get().points) ? get().points.length : 0,
          });
        } catch (e) {
          logEvent("warn", "ANDROID_FINALIZE_RECORDING_FREEZE_FAIL", {
            err: e?.message || String(e),
          });
        }
      }

      if (isAndroidNative && nativePlugin?.getRecordingSnapshot) {
        try {
          logger.info("HISTORY_SYNC", "ANDROID finalize: snapshot start");
          const snapshot = await withTimeout(
            nativePlugin.getRecordingSnapshot(),
            5000,
            "android_snapshot_timeout"
          );
          const nativePointsRaw = Array.isArray(snapshot?.points)
            ? snapshot.points
            : [];
          const nativePoints = sanitizeTrackPoints(
            nativePointsRaw.map((p) => ({
              lat: Number(p?.lat),
              lon: Number(p?.lon),
              ele: Number.isFinite(Number(p?.ele ?? p?.alt))
                ? Number(p.ele ?? p.alt)
                : null,
              acc: Number.isFinite(Number(p?.acc)) ? Number(p.acc) : null,
              spd: Number.isFinite(Number(p?.spd ?? p?.speed))
                ? Number(p.spd ?? p.speed)
                : 0,
              ts: Number(p?.ts || p?.time || 0),
            }))
          );
          const currentPoints = Array.isArray(get().points) ? get().points : [];
          const getTimeRange = (points) => {
            if (!Array.isArray(points) || points.length < 2) return null;
            const start = Number(points[0]?.ts || points[0]?.time || 0);
            const end = Number(
              points[points.length - 1]?.ts || points[points.length - 1]?.time || 0
            );
            return start > 0 && end >= start ? { start, end } : null;
          };
          const currentRange = getTimeRange(currentPoints);
          const nativeRange = getTimeRange(nativePoints);
          const coverageToleranceMs = 30000;
          const currentCoversNativeRange =
            !!currentRange &&
            !!nativeRange &&
            currentRange.start <= nativeRange.start + coverageToleranceMs &&
            currentRange.end >= nativeRange.end - coverageToleranceMs;
          const currentTrackIsIncomplete =
            currentPoints.length < 2 ||
            !currentCoversNativeRange ||
            nativePoints.length > currentPoints.length ||
            (nativePoints.length >= 20 &&
              currentPoints.length < Math.floor(nativePoints.length * 0.8));
          if (currentTrackIsIncomplete) {
            const recoveredPoints = filterAndroidTrackPoints(
              mergeRecoveredRecordingPoints(currentPoints, nativePoints)
            );
            set({ points: recoveredPoints });
            persistRecordingDraft(get(), { force: true });
            logEvent("info", "ANDROID_FINALIZE_SNAPSHOT_APPLIED", {
              nativePoints: nativePoints.length,
              currentPoints: currentPoints.length,
              recoveredPoints: recoveredPoints.length,
              currentRange,
              nativeRange,
              reason:
                currentPoints.length < 2
                  ? "missing_current_track"
                  : currentCoversNativeRange
                    ? "current_track_point_density_incomplete"
                  : "current_track_time_range_incomplete",
            });
            logger.info("HISTORY_SYNC", "ANDROID finalize: snapshot applied", {
              nativePoints: nativePoints.length,
              currentPoints: currentPoints.length,
              recoveredPoints: recoveredPoints.length,
            });
          } else {
            logEvent("info", "ANDROID_FINALIZE_SNAPSHOT_SKIPPED", {
              nativePoints: nativePoints.length,
              currentPoints: currentPoints.length,
              currentRange,
              nativeRange,
              reason: "preserve_complete_filtered_track",
            });
            logger.info("HISTORY_SYNC", "ANDROID finalize: snapshot skipped", {
              nativePoints: nativePoints.length,
              currentPoints: currentPoints.length,
            });
          }
        } catch (e) {
          logEvent("warn", "ANDROID_FINALIZE_SNAPSHOT_FAIL", {
            err: e?.message || String(e),
          });
          logger.warn("HISTORY_SYNC", "ANDROID finalize: snapshot fail", {
            err: e?.message || String(e),
          });
        }
      }

      if (isAndroidNative) {
        try {
          await nativePlugin?.stopTracking?.();
        } catch {}
        try {
          window.dispatchEvent(new CustomEvent("gps:buffer-clear"));
        } catch {}
        set({
          isRecording: false,
          isRecordingPaused: false,
          recordingPausedAt: null,
          recordingFinalizeInProgress: true,
        });
        logEvent("info", "ANDROID_FINALIZE_NATIVE_STOPPED_BEFORE_SAVE", {
          pointsNow: Array.isArray(get().points) ? get().points.length : 0,
        });
      }

      // 1️⃣ Guardar local
      logger.info("HISTORY_SYNC", "STOP iniciado");

      let saveResult = await get().saveTrackToHistory();
      if (
        !saveResult?.ok &&
        isAndroidNative
      ) {
        try {
          logger.warn("HISTORY_SYNC", "ANDROID finalize: primary save failed, trying recover", {
            reason: saveResult?.reason || "unknown",
          });
          const recover = await withTimeout(
            get().recoverRecordingFromNativeSnapshot(),
            5000,
            "android_recover_timeout"
          );
          logger.info("HISTORY_SYNC", "ANDROID finalize: recover result", recover || {});
          if (recover?.ok) {
            saveResult = await get().saveTrackToHistory();
            logger.info("HISTORY_SYNC", "ANDROID finalize: save retry result", {
              ok: !!saveResult?.ok,
              reason: saveResult?.reason || null,
            });
          }
        } catch (e) {
          logEvent("warn", "ANDROID_FINALIZE_RECOVER_FAIL", {
            err: e?.message || String(e),
          });
          logger.warn("HISTORY_SYNC", "ANDROID finalize: recover fail", {
            err: e?.message || String(e),
          });
        }
      }
      logger.debug("HISTORY_SYNC", "saveTrackToHistory()", saveResult);

      if (
        isAndroidNative &&
        !saveResult?.ok &&
        saveResult?.reason === "empty"
      ) {
        try {
          await nativePlugin?.clearRecordingSnapshot?.();
        } catch {}
        get().stopRecording();
        set({
          points: [],
          trailBase: [],
          trailData: null,
          totalDistance: 0,
          movingTime: 0,
          stoppedTime: 0,
          elevationGain: 0,
          elevationLoss: 0,
          recordingStartTime: null,
          recordingStopTime: null,
          isRecordingPaused: false,
          recordingPausedAt: null,
          recordingPausedDurationMs: 0,
          recordingResumePendingGap: false,
          recordingSessionId: null,
          recordingFinalizeInProgress: false,
        });
        clearActiveNavigationPersistence();
        logEvent("info", "HISTORY_EMPTY_RECORDING_DISCARDED", {
          platform: "android",
        });
        logger.info("HISTORY_SYNC", "STOP finalizado sem deslocação");
        return true;
      }

      if (saveResult?.ok && saveResult?.gamification?.eligible === false) {
        const speed = Number(saveResult?.gamification?.avgSpeedKmh || 0);
        const max = Number(saveResult?.gamification?.maxAvgSpeedKmh || 0);
        showToast(
          `Registo guardado, mas não entra na gamificação (velocidade média ${speed.toFixed(
            1
          )} km/h > ${max.toFixed(0)} km/h).`,
          "warning"
        );
      }

      if (!saveResult?.ok) {
        logEvent("error", "HISTORY_STOP_ABORT", {
          points: Array.isArray(saveResult?.points)
            ? saveResult.points.length
            : Array.isArray(get().points)
              ? get().points.length
              : 0,
          hasStart: !!get().recordingStartTime,
          reason: saveResult?.reason || "unknown",
        });
      }

      const auth = useAuthStore.getState();
      const user = auth?.user;

      if (isAndroidNative && saveResult?.ok) {
        try {
          nativePlugin?.setRecording?.({ recording: false })?.catch?.(() => {});
        } catch {}
        try {
          nativePlugin?.stopTracking?.()?.catch?.(() => {});
        } catch {}
        try {
          nativePlugin?.clearRecordingSnapshot?.()?.catch?.(() => {});
        } catch {}

        get().stopRecording();
        set({
          points: [],
          trailBase: [],
          trailData: null,
          totalDistance: 0,
          movingTime: 0,
          stoppedTime: 0,
          elevationGain: 0,
          elevationLoss: 0,
          recordingStartTime: null,
          recordingStopTime: null,
          isRecordingPaused: false,
          recordingPausedAt: null,
          recordingPausedDurationMs: 0,
          recordingResumePendingGap: false,
          recordingSessionId: null,
          recordingFinalizeInProgress: false,
        });
        clearActiveNavigationPersistence();

        if (user?.uid && saveResult?.gamification?.eligible === true) {
          await withTimeout(
            awardRecordingDistanceXp(
              user.uid,
              Number(saveResult?.distKm || 0),
              saveResult?.entryId || null,
              {
                activityMode: useActivityModeStore.getState()?.activityMode,
                recordedAt: saveResult?.ts || saveResult?.createdAt || Date.now(),
                elevationGainM: Number(saveResult?.dPlus || 0),
              }
            ),
            7000,
            "android_award_recording_xp_timeout"
          ).catch((e) => {
            logEvent("warn", "HISTORY_ANDROID_XP_SYNC_FAIL", {
              err: e?.message || String(e),
              entryId: saveResult?.entryId || null,
            });
          });
        }
        logger.info("HISTORY_SYNC", "STOP finalizado");
        return saveResult;
      }

      if (
        saveResult?.ok &&
        user?.uid &&
        saveResult?.gamification?.eligible === true &&
        !iosNetworkOptionalMode &&
        isProbablyOnline()
      ) {
        try {
          const xpAward = await withTimeout(
            awardRecordingDistanceXp(
              user.uid,
              Number(saveResult?.distKm || 0),
              saveResult?.entryId || null,
              {
                activityMode: useActivityModeStore.getState()?.activityMode,
                recordedAt: saveResult?.ts || saveResult?.createdAt || Date.now(),
                elevationGainM: Number(saveResult?.dPlus || 0),
              }
            ),
            7000,
            "award_recording_xp_timeout"
          );
          if (xpAward?.grantedXp > 0) {
            showToast(
              `+${xpAward.grantedXp} XP pela gravação (${Number(
                saveResult?.distKm || 0
              ).toFixed(2)} km).`,
              "success"
            );
          }
        } catch (e) {
          console.warn("⚠️ Falha ao atribuir XP de gravação:", e);
        }
      }

      // 2️⃣ Ver permissões
      const canUpload =
        user &&
        (user.role === "admin" ||
          user.plan === "admin" ||
          get().can("PanelHistory", "upload_gpx")?.ok);
      const isOnline =
        typeof navigator === "undefined" ? true : navigator.onLine !== false;

      logger.debug("HISTORY_SYNC", "Permissões avaliadas", {
        hasUser: !!user,
        uid: user?.uid,
        role: user?.role,
        plan: user?.plan,
        canUpload,
        isOnline,
      });

      const buildHistoryUploadFilename = (entryName, entryId) => {
        const normalizedBase = String(entryName || "registo")
          .replace(/^trekkin[-_\s]*/i, "")
          .replace(/^trekkin[-_\s]*/i, "")
          .trim();
        const safeName = String(normalizedBase || "registo")
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/[^a-zA-Z0-9_-]+/g, "-")
          .replace(/-+/g, "-")
          .replace(/^-|-$/g, "")
          .slice(0, 48);
        const safeId = String(entryId || Date.now())
          .replace(/[^a-zA-Z0-9_-]+/g, "")
          .slice(0, 32);
        return `Trekkin-${safeName || "registo"}-${safeId}.gpx`;
      };

      const uploadPointsAsGpx = async (points, entryName, entryId = null) => {
        const cleanPoints = sanitizeTrackPoints(points);
        if (cleanPoints.length < 2) return null;
        const gpxText = buildHistoryGpx(
          cleanPoints,
          entryName,
          { samples: useHeartRateStore.getState().samples || [] }
        );

        const gpxBlob = new Blob([gpxText], { type: "application/gpx+xml" });
        const filename = buildHistoryUploadFilename(entryName, entryId);

        const form = new FormData();
        form.append("uid", user.uid);
        form.append("filename", filename);
        form.append("file", gpxBlob, filename);

        const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
        const timeoutId = ctrl
          ? setTimeout(() => {
              try {
                ctrl.abort();
              } catch {}
            }, 12000)
          : null;
        const res = await fetch("https://www.trekkin.pt/app/app-upload/upload-history-gpx.php", {
          method: "POST",
          body: form,
          ...(ctrl ? { signal: ctrl.signal } : {}),
        }).finally(() => {
          if (timeoutId) clearTimeout(timeoutId);
        });

        const raw = await res.text();
        let json = null;
        try {
          json = JSON.parse(raw);
        } catch {
          return null;
        }
        if (!json?.ok) return null;
        return json;
      };

      const saveLiteHistoryEntry = (entry) => {
        try {
          const raw = localStorage.getItem("historygps") || "[]";
          const history = JSON.parse(raw);
          const list = (Array.isArray(history) ? history : []).filter(
            (item) => item?.id !== entry?.id
          );
          for (let i = 0; i <= list.length; i += 1) {
            try {
              localStorage.setItem("historygps", JSON.stringify([entry, ...list]));
              window.dispatchEvent(new CustomEvent("trekkin:history-updated"));
              return true;
            } catch {
              list.pop();
            }
          }
        } catch {}
        return false;
      };

      let requiredCloudBackupSaved = !saveResult?.requiresCloudBackup;
      if (
        (!saveResult?.ok || saveResult?.requiresCloudBackup) &&
        canUpload &&
        user?.uid &&
        isOnline
      ) {
        try {
          const pointsSrc = Array.isArray(saveResult?.points)
            ? saveResult.points
            : Array.isArray(get().points)
              ? get().points
              : [];

          const name =
            saveResult?.name ||
            formatHistoryDefaultName(get().recordingStartTime || Date.now());

          const json = await uploadPointsAsGpx(pointsSrc, name, saveResult?.entryId);

          if (json?.file) {
            const stats = saveResult?.stats || {};
            const statsDistKm = Number(stats.distKm ?? 0) || 0;
            const movingSecRaw = Number(stats.movingSec);
            const stoppedSecRaw = Number(stats.stoppedSec);
            const totalMinRaw = Number(stats.totalMin);
            const movingMinRaw = Number(stats.movingMin);
            const stoppedMinRaw = Number(stats.stoppedMin);

            const totalMin = Number.isFinite(totalMinRaw) && totalMinRaw > 0
              ? totalMinRaw
              : Number.isFinite(movingSecRaw) || Number.isFinite(stoppedSecRaw)
              ? ((Number(movingSecRaw || 0) + Number(stoppedSecRaw || 0)) / 60)
              : 0;

            const movingMin = Number.isFinite(movingMinRaw) && movingMinRaw > 0
              ? movingMinRaw
              : Number.isFinite(movingSecRaw)
              ? Number(movingSecRaw || 0) / 60
              : 0;

            const stoppedMin = Number.isFinite(stoppedMinRaw) && stoppedMinRaw >= 0
              ? stoppedMinRaw
              : Number.isFinite(stoppedSecRaw)
              ? Number(stoppedSecRaw || 0) / 60
              : 0;

            const speedRaw = Number(stats.speed);
            const speedComputed =
              statsDistKm > 0 && totalMin > 0 ? statsDistKm / (totalMin / 60) : 0;
            const avgSpeed = Number.isFinite(speedRaw) && speedRaw > 0 ? speedRaw : speedComputed;

            const liteEntry = {
              id: saveResult?.entryId || `rec-${Date.now()}`,
              uid: user?.uid || null,
              name,
              date: formatHistoryDefaultName(Date.now()),
              points: [],
              stats: {
                distKm: Number(statsDistKm.toFixed(2)),
                totalMin: Number(totalMin.toFixed(1)),
                movingMin: Number(movingMin.toFixed(1)),
                stoppedMin: Number(stoppedMin.toFixed(1)),
                speed: Number(avgSpeed.toFixed(1)),
                dPlus: Math.round(stats.dPlus || 0),
                caloriesKcal: Number(stats.caloriesKcal) || null,
                points: Array.isArray(pointsSrc) ? pointsSrc.length : 0,
              },
              cloud: {
                synced: true,
                file: json.file,
                syncedAt: Date.now(),
              },
              lite: true,
            };

            const liteOk = saveLiteHistoryEntry(liteEntry);
            requiredCloudBackupSaved = true;
            saveResult = {
              ...saveResult,
              ok: true,
              entryId: liteEntry.id,
              storageMode: "cloud-lite",
              requiresCloudBackup: false,
            };
            logEvent("info", "HISTORY_SAVE_LITE", {
              ok: liteOk,
              file: json.file,
              points: pointsSrc.length,
            });

            if (saveResult?.gamification?.eligible === true) {
              if (liteOk) {
                await get().syncMonthlyRankingKmFromHistory();
              } else {
                const rankingResult = await get().applyRecordingDistanceToMonthlyRanking({
                  entryId: liteEntry.id,
                  distKm: statsDistKm,
                  dPlus: Number(stats.dPlus || 0),
                  activityMode: useActivityModeStore.getState()?.activityMode,
                  recordedAt: get().recordingStartTime || Date.now(),
                });
                logEvent("info", "HISTORY_RANKING_QUOTA_FALLBACK", rankingResult);
              }
            }
          }
        } catch (e) {
          logEvent("error", "HISTORY_SAVE_LITE_ERROR", {
            err: e?.message || String(e),
          });
        }
      }

      if (!requiredCloudBackupSaved) {
        logEvent("error", "HISTORY_FINALIZE_PRESERVED_UNSAVED", {
          entryId: saveResult?.entryId || null,
          points: Array.isArray(saveResult?.points)
            ? saveResult.points.length
            : Array.isArray(get().points)
              ? get().points.length
              : 0,
          online: isOnline,
          canUpload: !!canUpload,
        });
        showToast(
          "Não foi possível guardar o percurso. A gravação foi preservada; liberta espaço ou liga-te à internet e tenta novamente.",
          "error"
        );
        set({ recordingFinalizeInProgress: false });
        return false;
      }

      if (canUpload && user?.uid && isOnline) {
        try {
          const localRaw = localStorage.getItem("historygps") || "[]";
          logger.debug("HISTORY_SYNC", "localStorage raw", {
            length: localRaw.length,
          });

          const local = JSON.parse(localRaw);

          const entry = [...local]
            .filter(
              (e) =>
                isHistoryEntryOwnedByUser(e, user.uid, { includeUnowned: false }) &&
                e.id?.startsWith("rec-") &&
                !e.cloud?.synced &&
                !e.cloud?.uploading &&
                Array.isArray(e?.points) &&
                e.points.length > 1
            )
            .sort((a, b) => {
              const ta = new Date(a.date).getTime() || 0;
              const tb = new Date(b.date).getTime() || 0;
              return tb - ta;
            })[0];

          logger.debug("HISTORY_SYNC", "Entry candidata", {
            id: entry?.id,
            date: entry?.date,
            points: entry?.points?.length,
            hasCloud: !!entry?.cloud,
          });

          const cleanPoints = sanitizeTrackPoints(entry?.points);
          if (cleanPoints.length > 1) {
            const markUploading = (flag) => {
              try {
                const rawHist = localStorage.getItem("historygps") || "[]";
                const parsedHist = JSON.parse(rawHist);
                if (!Array.isArray(parsedHist)) return;
                const patched = parsedHist.map((it) =>
                  it?.id === entry?.id
                    ? {
                        ...it,
                        cloud: {
                          ...(it?.cloud || {}),
                          uploading: !!flag,
                          uploadStartedAt: flag
                            ? Date.now()
                            : it?.cloud?.uploadStartedAt,
                        },
                      }
                    : it
                );
                localStorage.setItem("historygps", JSON.stringify(patched));
              } catch {}
            };
            markUploading(true);
            logger.info("HISTORY_SYNC", "A construir GPX", {
              points: cleanPoints.length,
            });

            // The native GPX contains the complete track and BPM timeline.
            // Rebuilding from the local preview used to upload only ~700 GPS
            // points and silently dropped every heart-rate sample on iOS.
            let gpxText = await readHistoryGpxNative(entry);
            if (!gpxText && typeof entry?.gpx === "string") gpxText = entry.gpx;
            if (!gpxText) {
              gpxText = buildHistoryGpx(cleanPoints, entry.name, entry.heartRate);
            }

            const gpxBlob = new Blob([gpxText], {
              type: "application/gpx+xml",
            });

            logger.debug("HISTORY_SYNC", "GPX Blob criado", {
              size: gpxBlob.size,
              type: gpxBlob.type,
              isBlob: gpxBlob instanceof Blob,
            });

            const filename = buildHistoryUploadFilename(entry?.name, entry?.id);

            const form = new FormData();
            form.append("uid", user.uid);
            form.append("filename", filename);
            form.append("file", gpxBlob, filename);

            // 🔍 INSPECIONAR FORMDATA (CRÍTICO PARA iOS)
            const fdDump = [];
            for (const [k, v] of form.entries()) {
              fdDump.push({
                key: k,
                kind:
                  v instanceof File
                    ? "File"
                    : v instanceof Blob
                    ? "Blob"
                    : typeof v,
                name: v?.name,
                size: v?.size,
                type: v?.type,
              });
            }
            logger.debug("HISTORY_SYNC", "FormData preparado", fdDump);

            logger.info("HISTORY_SYNC", "A iniciar fetch upload");

            const res = await fetch("https://www.trekkin.pt/app/app-upload/upload-history-gpx.php", {
              method: "POST",
              body: form,
            });

            logger.info("HISTORY_SYNC", "Fetch concluído", {
              status: res.status,
              ok: res.ok,
            });

            let raw = "";
            try {
              raw = await res.text();
              logger.debug("HISTORY_SYNC", "Resposta RAW", raw);
            } catch (e) {
              markUploading(false);
              logger.error("HISTORY_SYNC", "Erro a ler resposta", e);
              return;
            }

            let json = null;
            try {
              json = JSON.parse(raw);
            } catch {
              markUploading(false);
              logger.warn("HISTORY_SYNC", "Resposta não é JSON válido", raw);
              return;
            }

            if (!json?.ok) {
              markUploading(false);
              logger.warn("HISTORY_SYNC", "Upload recusado pelo servidor", json);
              return;
            }

            const updated = local.map((it) =>
              it.id === entry.id
                ? {
                    ...it,
                    cloud: {
                      ...(it?.cloud || {}),
                      synced: true,
                      file: json.file,
                      syncedAt: Date.now(),
                      uploading: false,
                    },
                  }
                : it
            );

            localStorage.setItem("historygps", JSON.stringify(updated));
            window.dispatchEvent(new Event("trekkin:history-updated"));

            logger.info("HISTORY_SYNC", "Histórico sincronizado com sucesso", {
              id: entry.id,
              file: json.file,
            });
          } else {
            logger.warn("HISTORY_SYNC", "Entry inválida ou sem pontos suficientes");
          }
        } catch (e) {
          try {
            const rawHist = localStorage.getItem("historygps") || "[]";
            const parsedHist = JSON.parse(rawHist);
            if (Array.isArray(parsedHist)) {
              const patched = parsedHist.map((it) =>
                it?.id === entry?.id
                  ? {
                      ...it,
                      cloud: { ...(it?.cloud || {}), uploading: false },
                    }
                  : it
              );
              localStorage.setItem("historygps", JSON.stringify(patched));
            }
          } catch {}
          logger.error("HISTORY_SYNC", "Erro geral no sync", e);
        }
      } else if (!iosNetworkOptionalMode && !isOnline) {
        logger.info("HISTORY_SYNC", "Sem internet: upload adiado (ficou guardado localmente)");
        if (saveResult?.ok) {
          showToast("Registo guardado offline. Vai sincronizar quando houver internet.", "info");
        }
      } else if (iosNetworkOptionalMode) {
        logger.info("HISTORY_SYNC", "iOS network-optional finalize: skip network steps");
        if (saveResult?.ok) {
          showToast("Registo guardado localmente no iOS. Sincronização será feita depois.", "info");
        }
      } else {
        logger.warn("HISTORY_SYNC", "Upload não permitido ou sem UID");
      }

      // 3️⃣ ANDROID: parar GPS nativo
      try {
        if (isAndroidNative) {
          try {
            await nativePlugin?.setRecording?.({ recording: false });
          } catch {}
        }
        await nativePlugin?.stopTracking?.();
      } catch {}

      if (saveResult?.ok) {
        try {
          const plugin =
            window?.Capacitor?.Plugins?.GPSNativePlugin ||
            window?.Capacitor?.Plugins?.GPSNative;
          await plugin?.clearRecordingSnapshot?.();
        } catch {}
      }

      // 4️⃣ Parar gravação
      get().stopRecording();

      // 5️⃣ Limpar sessão
      set({
        points: [],
        trailBase: [],
        trailData: null,
        totalDistance: 0,
        movingTime: 0,
        stoppedTime: 0,
        elevationGain: 0,
        elevationLoss: 0,
        recordingStartTime: null,
        recordingStopTime: null,
        isRecordingPaused: false,
        recordingPausedAt: null,
        recordingPausedDurationMs: 0,
        recordingResumePendingGap: false,
        recordingSessionId: null,
        recordingFinalizeInProgress: false,
      });
      clearActiveNavigationPersistence();

      logger.info("HISTORY_SYNC", "STOP finalizado");

      return saveResult?.ok ? saveResult : false;
      } finally {
        set({ recordingFinalizeInProgress: false });
      }
    },

    resetStats: () =>
      set({
        totalDistance: 0,
        movingTime: 0,
        stoppedTime: 0,
        lastUpdate: null,
        elevationGain: 0,
        elevationLoss: 0,
      }),

    //------------------------------------------------------------------
    // 📅 EVENTOS — payload partilhado
    //------------------------------------------------------------------
    eventPayload: null,
    setEventPayload: (payload) => set({ eventPayload: payload || null }),

    setActivePanel: (name, props = {}) => {
      const { openPanel } = get();
      if (typeof openPanel === "function") {
        // 🔒 nunca deixar payload antigo vazar
        set({ eventPayload: null });
        openPanel(name, props);
      }
    },
    
    //------------------------------------------------------------------
    // 🧩 Getter obrigatório para PanelShell — FIX CRÍTICO
    //------------------------------------------------------------------
    getCurrentPanel: () => {
      const { currentPanel } = get();
      return currentPanel || null;
    },

    //------------------------------------------------------------------
    // 🧠 🔥 Métricas
    //------------------------------------------------------------------
    getMetrics() {
      const s = get();
      return {
        totalTime: s.movingTime + s.stoppedTime,
        movingTime: s.movingTime,
        stoppedTime: s.stoppedTime,
        currentSpeed: s.lastSpeed,
        distanceKm: s.totalDistance / 1000,
        elevationGain: s.elevationGain,
        points: s.points.length,
      };
    },

    //------------------------------------------------------------------
    // 🛰️ Listener GPSHybrid (NO-OP)
    //------------------------------------------------------------------
    _gpsListenerActive: false,

    _attachGPSListener() {
      const state = get();
      if (state._gpsListenerActive) return;
      set({ _gpsListenerActive: true });
      console.log("🔗 _attachGPSListener chamado (NO-OP, Bridge ativo)");
    },

    //------------------------------------------------------------------
    // 🔄 LIVE
    //------------------------------------------------------------------
    _lastLivePayload: null,
    _liveBusy: false,

    sendLiveUpdate: debounce(async (payload) => {
      const gs = useGroupStore.getState();

      const guideOnline = !!(
        gs?.guideOnline ||
        gs?.isGuideOnline ||
        gs?.meIsGuide
      );

      const activeGroupId =
        gs?.activeGroupId ||
        gs?.activeGroup?.id ||
        gs?.currentGroupId ||
        (gs?.currentGroup && (gs.currentGroup.id || gs.currentGroup));

      if (!guideOnline || !activeGroupId) {
        set({ _lastLivePayload: payload });
        return;
      }

      const api = getAPIBase();
      const url = `${api}/groups/${encodeURIComponent(activeGroupId)}/live`;

      try {
        set({ _liveBusy: true });
        const res = await fetch(url, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          console.warn("⚠️ Live update falhou:", res.status, txt);
          set({ _lastLivePayload: payload });
        } else {
          set({ _lastLivePayload: null });
        }
      } catch (e) {
        console.warn("⚠️ Live update erro:", e);
        set({ _lastLivePayload: payload });
      } finally {
        set({ _liveBusy: false });
      }
    }, 900),
  }));

if (recoveredNavigationSession) {
  setTimeout(() => {
    logEvent("info", "NAVIGATION_SESSION_RESTORED", {
      type: recoveredNavigationSession.type,
      ageMs: Math.max(
        0,
        Date.now() - Number(recoveredNavigationSession.updatedAt || Date.now())
      ),
      routePoints: Array.isArray(recoveredNavigationSession.route)
        ? recoveredNavigationSession.route.length
        : 0,
      trailPoints: Array.isArray(recoveredNavigationSession.trailBase)
        ? recoveredNavigationSession.trailBase.length
        : 0,
      queuedWaypoints: Array.isArray(recoveredNavigationSession.queue)
        ? recoveredNavigationSession.queue.length
        : 0,
    });
  }, 0);
}

  // --------------------------------------------------
  // 🔙 RETORNO DO mapSelectPoint (FIX ABSOLUTO — PRESERVA eventId)
  // --------------------------------------------------
  usePanelsStore.subscribe((state, prevState) => {
    const current = state.currentPanel;
    const previous = prevState?.currentPanel;

    // Só ativa quando SAÍMOS do mapSelectPoint e há callbackResult
    if (
      previous?.name === "mapSelectPoint" &&
      current?.props?.callbackResult
    ) {
      const result = current.props.callbackResult;

      // 🟢 1) Determinar o painel de retorno
      const returnTo =
        prevState._returnPanel ||
        previous.props?.returnPanel ||
        "eventEdit";

      // 🟢 2) Extrair o eventId verdadeiro
      const originalEventId =
        previous.props?.eventId ||
        prevState._returnProps?.eventId ||
        result?.eventId ||
        null;

      // 🟢 3) Construir props finais SEM NUNCA perder o eventId
      const returnProps = {
        eventId: originalEventId,
        ...(prevState._returnProps || {}),
        ...(previous.props?.returnProps || {}),
        ...(result || {}),
      };

      // Limpar registos internos
      usePanelsStore.setState({
        _returnPanel: null,
        _returnProps: null,
      });

      console.log(
        "🔙 [mapSelectPoint] → retornar para:",
        returnTo,
        "props:",
        returnProps
      );

      // 🟢 4) Voltar diretamente ao painel correto
      usePanelsStore.getState().openPanel(returnTo, returnProps);
    }
  });

  //------------------------------------------------------------------
  // 🧭 Login Listener + Permissões por Role
  //------------------------------------------------------------------
export function usePanelsLoginListener() {
    const { openPanel, resetToMap } = usePanelsStore();
    const { user, authReady } = useAuthStore();

    useEffect(() => {
      // Evita flicker de login no arranque enquanto Firebase ainda não resolveu sessão.
      if (!authReady) return;

      const panelsState = usePanelsStore.getState();
      const role = getCurrentRole();
      const currentName = panelsState?.currentPanel?.name || null;
      const hasCurrentPanel = !!currentName;
      const authPanels = new Set(["intro", "login", "resetPassword", "setNewPassword"]);

      // Atualizar permissões sempre que o user muda
      if (typeof panelsState.setPermissionsRole === "function") {
        panelsState.setPermissionsRole(role);
      }

      if (user) {
        // Com sessão ativa, a app deve ficar no mapa por defeito.
        // Não sobrescrever deep-links/notificações (ex.: eventDetails/directChat)
        // quando já existe um painel funcional aberto.
        if (authPanels.has(currentName)) {
          console.log("🧭 User autenticado → mapa");
          resetToMap();
        } else if (!hasCurrentPanel) {
          console.log("🧭 User autenticado → mapa (sem painel)");
        } else {
          console.log(`🧭 User autenticado → manter painel atual (${currentName})`);
        }
      } else {
        if (currentName !== "login") {
          console.log("🧭 Sem sessão → login");
          openPanel("login");
        }
      }
    }, [authReady, user, openPanel, resetToMap]);

    useEffect(() => {
      const handler = () => {
        console.log("🧭 trekkin:close-login → mapa");
        resetToMap();
      };
      window.addEventListener("trekkin:close-login", handler);
      return () =>
        window.removeEventListener("trekkin:close-login", handler);
    }, [resetToMap]);
  }

  //------------------------------------------------------------------
  // 🛰️ Ativar listener GPSHybrid
  //------------------------------------------------------------------
  usePanelsStore.getState()._attachGPSListener();
  //------------------------------------------------------------------
  // 🔐 Carregar permissões globais (comparação de planos)
  //------------------------------------------------------------------
  usePanelsStore.getState().loadAllPermissions();
